<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap ga-2">
      <h1 class="text-h5">{{ $t('adminUsers.title') }}</h1>
      <div class="d-flex ga-2">
        <v-btn
          color="warning"
          variant="tonal"
          :loading="normalizingPhones"
          prepend-icon="mdi-phone-check"
          @click="normalizePhones"
        >
          {{ $t('adminUsers.fixPhones') }}
        </v-btn>
        <v-btn color="primary" @click="openCreateDialog">
          {{ $t('admin.createUser') }}
        </v-btn>
      </div>
    </div>

    <!-- Online count and Filters -->
    <v-card class="mb-4" variant="tonal">
      <v-card-text class="pa-3">
        <!-- Online count badge -->
        <div class="d-flex align-center gap-2 mb-3">
          <v-btn 
            :color="filters.sortBy === 'online' ? 'success' : 'success'" 
            :variant="filters.sortBy === 'online' ? 'elevated' : 'tonal'"
            size="small"
            @click="filters.sortBy === 'online' ? clearSort() : sortByOnline()"
            prepend-icon="mdi-account-multiple"
            style="text-transform: none;"
          >
            <strong>{{ onlineCount }}</strong>
            <span class="ml-1">{{ $t('adminUsers.online') }}</span>
            <v-icon end size="x-small">{{ filters.sortBy === 'online' ? 'mdi-close' : 'mdi-sort' }}</v-icon>
          </v-btn>
        </div>

        <div class="d-flex flex-wrap align-end gap-2">
          <v-text-field 
            v-model="filters.search" 
            :label="$t('adminUsers.searchPlaceholder')" 
            clearable 
            @update:model-value="debouncedSearch"
            @click:clear="applyFilters"
            hide-details
            density="compact"
            style="min-width: 200px; flex: 1 1 200px;"
          />
          <v-select 
            v-model="filters.role" 
            :items="roleItems" 
            :label="$t('admin.userRole')" 
            clearable 
            item-title="title" 
            item-value="value"
            hide-details
            density="compact"
            style="min-width: 130px; flex: 0 1 160px;"
          />
          <v-select 
            v-model="filters.status" 
            :items="statusItems" 
            :label="$t('common.status')" 
            clearable 
            item-title="title" 
            item-value="value"
            hide-details
            density="compact"
            style="min-width: 100px; flex: 0 1 130px;"
          />
          <v-btn 
            color="primary" 
            :loading="loading" 
            @click="applyFilters"
            prepend-icon="mdi-reload"
            size="small"
          >
            {{ $t('adminUsers.loadUsers') }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Desktop Table View -->
    <v-card class="d-none d-md-block">
      <v-data-table-server 
        :headers="headers" 
        :items="users" 
        :items-length="pagination.total"
        :loading="loading" 
        item-key="id" 
        :items-per-page="pagination.limit" 
        :sort-by="tableSortBy"
        @update:sort-by="handleSort"
        hide-default-footer
        density="compact"
        class="elevation-1 compact-table"
      >
        <template #item.isOnline="{ item }">
          <v-tooltip :text="item.isInTest ? $t('adminUsers.inTest') : (item.isOnline ? $t('adminUsers.online') : $t('adminUsers.offline'))">
            <template #activator="{ props }">
              <div v-bind="props" class="d-flex align-center">
                <v-icon 
                  :color="item.isInTest ? 'warning' : (item.isOnline ? 'success' : 'grey')" 
                  size="small"
                  :class="{ 'animate-pulse': item.isInTest }"
                >
                  {{ item.isInTest ? 'mdi-pencil-circle' : (item.isOnline ? 'mdi-circle' : 'mdi-circle-outline') }}
                </v-icon>
              </div>
            </template>
          </v-tooltip>
        </template>
        <template #item.phone="{ item }">
          <div class="d-flex align-center gap-2">
            <span>{{ item.phone }}</span>
            <v-btn size="x-small" variant="text" icon="mdi-content-copy" @click="copy(item.phone)" />
          </div>
        </template>
        <template #item.appVersion="{ item }">
          <v-chip
            v-if="item.lastSession?.appVersion"
            size="small"
            variant="tonal"
            color="info"
          >
            v{{ item.lastSession.appVersion }}
          </v-chip>
          <span v-else class="text-medium-emphasis text-caption">—</span>
        </template>
        <template #item.testStats="{ item }">
          <div v-if="item.testStats && item.testStats.total > 0" class="d-flex flex-column cursor-pointer" @click="openRoundsDialog(item)">
            <div class="d-flex align-center gap-1">
              <span class="text-success font-weight-bold">{{ item.testStats.correct }}</span>
              <span class="text-grey">/</span>
              <span class="text-error font-weight-bold">{{ item.testStats.incorrect }}</span>
              <span class="text-grey">/</span>
              <span class="text-info font-weight-bold">{{ item.testStats.percentage }}%</span>
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.testStats.answered }}/{{ item.testStats.totalTests }} ({{ item.testStats.progress }}%)
              <span v-if="item.testStats.totalRounds > 1" class="text-info font-weight-bold">
                {{ $t('adminUsers.rounds.round') }}: {{ item.testStats.round }}
              </span>
            </div>
          </div>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
        <template #item.role="{ item }">
          <div class="d-flex align-center" style="gap: 4px;">
            <v-select
              v-model="item.role"
              :items="roleItems"
              density="compact"
              hide-details
              item-title="title"
              item-value="value"
              variant="plain"
              class="compact-role-select flex-grow-1"
              @click="savePreviousRole(item)"
              @update:model-value="(val) => confirmRoleChange(item, val)"
            />
            <v-tooltip v-if="item.telegramAccount?.chatId" location="top">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="14"
                  :color="item.telegramAccount?.notificationsEnabled === false ? 'grey' : '#229ED9'"
                  class="telegram-linked-icon"
                >mdi-telegram</v-icon>
              </template>
              <span>{{ item.telegramAccount?.notificationsEnabled === false
                ? $t('adminUsers.telegramLinkedMuted')
                : $t('adminUsers.telegramLinked') }}</span>
            </v-tooltip>
          </div>
          <div v-if="item.role === 'INSTRUCTOR' && instructorAddresses(item).length" class="d-flex align-center mt-1" style="gap: 3px;">
            <v-icon size="12" color="teal">mdi-map-marker</v-icon>
            <span class="text-caption text-teal text-truncate" style="max-width: 120px;">{{ formatInstructorAddresses(item) }}</span>
          </div>
          <div
            v-if="['STUDENT', 'LIMITED_STUDENT'].includes(item.role) && item.instructor?.fio"
            class="d-flex align-center mt-1"
            style="gap: 3px;"
          >
            <v-icon size="12" color="primary">mdi-account-tie</v-icon>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <span
                  v-bind="props"
                  class="text-caption text-primary text-truncate"
                  style="max-width: 120px;"
                >{{ item.instructor.fio }}</span>
              </template>
              <span>{{ $t('adminUsers.assignedInstructor') }}: {{ item.instructor.fio }}</span>
            </v-tooltip>
          </div>
        </template>
        <template #item.status="{ item }">
          <v-select
            v-model="item.status"
            :items="statusItems"
            density="compact"
            hide-details
            item-title="title"
            item-value="value"
            variant="plain"
            class="compact-status-select"
            @click="savePreviousStatus(item)"
            @update:model-value="(val) => updateUserStatus(item, val)"
          >
            <template #selection="{ item: selected }">
              <v-chip :color="getStatusColor(selected.value)" size="small">
                {{ getStatusText(selected.value) }}
              </v-chip>
            </template>
          </v-select>
        </template>
        <template #item.expiryDate="{ item }">
          <div class="d-flex align-center gap-2">
            <div v-if="item.expiryDate">
              <v-chip 
                :color="getExpiryChipColor(item.expiryDate)" 
                size="small"
                variant="elevated"
              >
                <v-icon start size="small">mdi-calendar-clock</v-icon>
                {{ formatExpiryDate(item.expiryDate) }}
              </v-chip>
            </div>
            <span v-else class="text-medium-emphasis text-caption">{{ $t('adminUsers.unlimited') }}</span>
            <v-btn 
              size="x-small" 
              variant="text" 
              icon="mdi-calendar-edit" 
              @click="openExpiryDialog(item)"
              :title="item.expiryDate ? $t('adminUsers.changeExpiry') : $t('adminUsers.setExpiry')"
            />
          </div>
        </template>
        <template #item.creator="{ item }">
          <div v-if="item.creator">
            <v-tooltip :text="`${item.creator.fio} (${item.creator.role})`">
              <template #activator="{ props }">
                <v-chip v-bind="props" size="small" variant="tonal" color="primary">
                  <v-icon start size="small">mdi-account-plus</v-icon>
                  {{ item.creator.fio }}
                </v-chip>
              </template>
            </v-tooltip>
            <div class="text-caption text-medium-emphasis mt-1">{{ formatShortDateTime(item.createdAt) }}</div>
          </div>
          <div v-else>
            <v-chip size="small" variant="tonal" color="success">
              <v-icon start size="small">mdi-account-check</v-icon>
              {{ $t('adminUsers.selfRegistered') }}
            </v-chip>
            <div class="text-caption text-medium-emphasis mt-1">{{ formatShortDateTime(item.createdAt) }}</div>
          </div>
        </template>
        <template #item.updater="{ item }">
          <div v-if="item.updater">
            <v-tooltip :text="`${item.updater.fio} (${item.updater.role})`">
              <template #activator="{ props }">
                <v-chip v-bind="props" size="small" variant="tonal" color="info">
                  <v-icon start size="small">mdi-account-edit</v-icon>
                  {{ item.updater.fio }}
                </v-chip>
              </template>
            </v-tooltip>
            <div class="text-caption text-medium-emphasis mt-1">{{ formatShortDateTime(item.updatedAt) }}</div>
          </div>
          <span v-else class="text-medium-emphasis text-caption">—</span>
        </template>
        <template #item.lastSession="{ item }">
          <div v-if="item.lastSession" class="d-flex flex-column">
            <div v-if="parseUserAgent(item.lastSession.userAgent)" class="d-flex align-center gap-1 mb-1">
              <v-tooltip :text="item.lastSession.userAgent" max-width="400">
                <template #activator="{ props }">
                  <span v-bind="props" class="d-flex align-center gap-1">
                    <v-icon size="14" :color="parseUserAgent(item.lastSession.userAgent).deviceColor">
                      {{ parseUserAgent(item.lastSession.userAgent).deviceIcon }}
                    </v-icon>
                    <v-icon size="12">{{ parseUserAgent(item.lastSession.userAgent).browserIcon }}</v-icon>
                    <span class="text-caption text-medium-emphasis">{{ parseUserAgent(item.lastSession.userAgent).label }}</span>
                  </span>
                </template>
              </v-tooltip>
            </div>
            <div class="d-flex align-center gap-1">
              <v-icon size="12" color="success">mdi-login</v-icon>
              <span class="text-caption">{{ formatSessionTime(item.lastSession.loginAt) }}</span>
            </div>
            <div v-if="item.lastSession.logoutAt" class="d-flex align-center gap-1">
              <v-icon size="12" color="error">mdi-logout</v-icon>
              <span class="text-caption">{{ formatSessionTime(item.lastSession.logoutAt) }}</span>
            </div>
            <div v-else-if="item.isOnline" class="d-flex align-center gap-1">
              <v-icon size="12" color="success">mdi-circle</v-icon>
              <span class="text-caption text-success">{{ $t('adminUsers.currentlyOnline') }}</span>
            </div>
          </div>
          <span v-else class="text-medium-emphasis text-caption">—</span>
        </template>
        <template #item.actions="{ item }">
          <v-btn size="small" icon="mdi-dots-vertical" variant="text" @click="openActionsDialog(item)" />
        </template>

        <template #bottom>
          <div class="d-flex align-center justify-space-between pa-4">
            <div class="text-body-2 text-medium-emphasis">
              {{ $t('adminUsers.showingPage') }} {{ pagination.page }} / {{ totalPages }} 
              ({{ $t('adminUsers.totalUsers') }}: {{ pagination.total }})
            </div>
            <v-pagination 
              v-model="pagination.page" 
              :length="totalPages" 
              :total-visible="5"
              @update:model-value="loadUsers" 
            />
          </div>
        </template>
      </v-data-table-server>
    </v-card>
    
    <!-- Mobile Card View -->
    <div class="d-md-none">
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
        <div class="mt-2 text-body-2 text-medium-emphasis">{{ $t('common.loading') }}</div>
      </div>
      
      <div v-else-if="users.length === 0" class="text-center py-8">
        <v-icon size="64" color="grey-lighten-2">mdi-account-search</v-icon>
        <div class="mt-2 text-body-1 text-medium-emphasis">{{ $t('adminUsers.noUsers') }}</div>
      </div>
      
      <div v-else class="user-cards">
        <v-card 
          v-for="user in users" 
          :key="user.id"
          class="mb-3"
          variant="outlined"
          :ripple="false"
        >
          <v-card-text class="pa-3 pb-2 user-card-body">
            <!-- Row 1: Online dot + Name (multi-line) + ID | Status pill (top-right) -->
            <div class="user-card-header">
              <div class="user-card-name-block">
                <v-icon
                  :color="user.isInTest ? 'warning' : (user.isOnline ? 'success' : 'grey')"
                  size="12"
                  class="flex-shrink-0 user-online-dot"
                >
                  {{ user.isInTest ? 'mdi-pencil-circle' : (user.isOnline ? 'mdi-circle' : 'mdi-circle-outline') }}
                </v-icon>
                <div class="user-name-wrap">
                  <span class="user-name">{{ user.fio || $t('adminUsers.noName') }}</span>
                  <span class="user-id">#{{ user.id }}</span>
                </div>
              </div>
              <v-select
                v-model="user.status"
                :items="statusItems"
                density="compact"
                hide-details
                item-title="title"
                item-value="value"
                variant="plain"
                class="mobile-status-select user-status-select"
                @click="savePreviousStatus(user)"
                @update:model-value="(val) => updateUserStatus(user, val)"
              >
                <template #selection="{ item: selected }">
                  <v-chip :color="getStatusColor(selected.value)" size="x-small" variant="elevated">
                    {{ getStatusText(selected.value) }}
                  </v-chip>
                </template>
              </v-select>
            </div>

            <!-- Row 2: Phone + Email -->
            <div class="d-flex flex-column mb-3" style="gap: 4px;">
              <div class="d-flex align-center">
                <v-icon size="14" class="me-1 text-medium-emphasis flex-shrink-0">mdi-phone</v-icon>
                <span class="text-caption flex-shrink-0">{{ user.phone }}</span>
                <v-btn size="x-small" variant="text" icon="mdi-content-copy" density="compact" class="flex-shrink-0 ms-1" @click="copy(user.phone)" />
              </div>
              <div v-if="user.email" class="d-flex align-center" style="min-width: 0;">
                <v-icon size="14" class="me-1 text-medium-emphasis flex-shrink-0">mdi-email</v-icon>
                <span class="text-caption text-truncate">{{ user.email }}</span>
              </div>
            </div>

            <!-- Row 3: Role + Test Stats -->
            <div class="d-flex align-center justify-space-between mb-3" style="gap: 8px;">
              <v-select 
                v-model="user.role" 
                :items="roleItems" 
                density="compact" 
                hide-details
                variant="outlined"
                item-title="title" 
                item-value="value"
                style="max-width: 130px; flex-shrink: 0;"
                @click="savePreviousRole(user)"
                @update:model-value="(val) => confirmRoleChange(user, val)" 
              />
              <div v-if="user.testStats && user.testStats.total > 0" class="text-right flex-shrink-0 cursor-pointer" @click="openRoundsDialog(user)">
                <div class="d-inline-flex align-center" style="gap: 2px;">
                  <span class="text-caption font-weight-bold" style="color: #4CAF50;">{{ user.testStats.correct }}</span>
                  <span class="text-caption text-medium-emphasis">/</span>
                  <span class="text-caption font-weight-bold" style="color: #F44336;">{{ user.testStats.incorrect }}</span>
                  <span class="text-caption text-medium-emphasis">/</span>
                  <span class="text-caption font-weight-bold" style="color: #2196F3;">{{ user.testStats.percentage }}%</span>
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ user.testStats.answered }}/{{ user.testStats.totalTests }} ({{ user.testStats.progress }}%)
                  <span v-if="user.testStats.totalRounds > 1" class="text-info font-weight-bold">
                    {{ $t('adminUsers.rounds.round') }}: {{ user.testStats.round }}
                  </span>
                </div>
              </div>
              <span v-else class="text-caption text-medium-emphasis">—</span>
            </div>

            <!-- Row 3.5: Instructor Address -->
            <div v-if="user.role === 'INSTRUCTOR' && instructorAddresses(user).length" class="d-flex align-center mb-2" style="gap: 4px;">
              <v-icon size="14" color="teal">mdi-map-marker</v-icon>
              <span class="text-caption text-teal">{{ formatInstructorAddresses(user) }}</span>
            </div>

            <!-- Row 4: Expiry -->
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center" style="gap: 4px;">
                <v-icon size="14" class="text-medium-emphasis flex-shrink-0">mdi-calendar-clock</v-icon>
                <div v-if="user.expiryDate">
                  <v-chip :color="getExpiryChipColor(user.expiryDate)" size="x-small" variant="elevated">
                    <v-icon start size="x-small">mdi-calendar-clock</v-icon>
                    {{ formatExpiryDateShort(user.expiryDate) }}
                  </v-chip>
                </div>
                <span v-else class="text-caption text-medium-emphasis">{{ $t('adminUsers.unlimited') }}</span>
                <v-btn size="x-small" variant="text" icon="mdi-calendar-edit" density="compact" @click="openExpiryDialog(user)" />
              </div>
            </div>

            <!-- Row 5: Created / Updated (chips like desktop) -->
            <div class="d-flex align-center flex-wrap mb-3" style="gap: 6px;">
              <v-chip v-if="user.creator" size="x-small" variant="tonal" color="primary">
                <v-icon start size="x-small">mdi-account-plus</v-icon>
                {{ user.creator.fio }}
              </v-chip>
              <v-chip v-else size="x-small" variant="tonal" color="success">
                <v-icon start size="x-small">mdi-account-check</v-icon>
                {{ $t('adminUsers.selfRegistered') }}
              </v-chip>
              <v-chip 
                v-if="user.updater && (!user.creator || user.updater.id !== user.creator.id)" 
                size="x-small" variant="tonal" color="info"
              >
                <v-icon start size="x-small">mdi-account-edit</v-icon>
                {{ user.updater.fio }}
              </v-chip>
            </div>

            <!-- Row 6: Login / Logout times -->
            <div v-if="user.lastSession" class="d-flex align-center flex-wrap" style="gap: 6px;">
              <span v-if="parseUserAgent(user.lastSession.userAgent)" class="d-flex align-center gap-1">
                <v-icon size="14" :color="parseUserAgent(user.lastSession.userAgent).deviceColor">
                  {{ parseUserAgent(user.lastSession.userAgent).deviceIcon }}
                </v-icon>
                <v-icon size="12">{{ parseUserAgent(user.lastSession.userAgent).browserIcon }}</v-icon>
                <span class="text-caption text-medium-emphasis">{{ parseUserAgent(user.lastSession.userAgent).label }}</span>
              </span>
              <v-chip
                v-if="user.lastSession.appVersion"
                size="x-small"
                variant="tonal"
                color="info"
              >
                v{{ user.lastSession.appVersion }}
              </v-chip>
              <div class="d-flex align-center gap-1">
                <v-icon size="12" color="success">mdi-login</v-icon>
                <span class="text-caption">{{ formatSessionTime(user.lastSession.loginAt) }}</span>
              </div>
              <div v-if="user.lastSession.logoutAt" class="d-flex align-center gap-1">
                <v-icon size="12" color="error">mdi-logout</v-icon>
                <span class="text-caption">{{ formatSessionTime(user.lastSession.logoutAt) }}</span>
              </div>
              <span v-else-if="user.isOnline" class="text-caption text-success font-weight-bold">{{ $t('adminUsers.online') }}</span>
            </div>
          </v-card-text>
          
          <!-- Actions -->
          <v-divider />
          <div class="d-flex align-center justify-center px-2 py-1">
            <v-btn size="small" variant="tonal" @click="openActionsDialog(user)" prepend-icon="mdi-dots-horizontal">
              {{ $t('common.actions') }}
            </v-btn>
          </div>
        </v-card>
      </div>
      
      <!-- Mobile Pagination -->
      <div class="text-center mt-4">
        <div class="text-body-2 text-medium-emphasis mb-2">
          {{ $t('adminUsers.showingPage') }} {{ pagination.page }} / {{ totalPages }} 
          ({{ $t('adminUsers.totalUsers') }}: {{ pagination.total }})
        </div>
        <v-pagination 
          v-model="pagination.page" 
          :length="totalPages" 
          :total-visible="5"
          @update:model-value="loadUsers" 
        />
      </div>
    </div>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="editDialog.visible" max-width="480">
      <v-card>
  <v-card-title>{{ editDialog.mode === 'create' ? $t('admin.createUser') : $t('admin.editUser') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editDialog.form.fio" :label="$t('common.fullName')" required />
          <PhoneInput v-model="editDialog.form.phone" :label="$t('common.phone')" :rules="phoneRules" />
          <v-text-field 
            v-model="editDialog.form.email" 
            label="Email" 
            :rules="emailRules"
          />
          <v-text-field 
            v-if="editDialog.mode === 'create'" 
            v-model="editDialog.form.password" 
            :label="$t('auth.password')" 
            :type="showPassword ? 'text' : 'password'"
            required 
            :hint="$t('adminUsers.setPasswordHint')"
            persistent-hint
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
          />
          <v-select
            v-model="editDialog.form.role"
            :items="roleItems"
            :label="$t('admin.userRole')"
            item-title="title"
            item-value="value"
          />

          <!-- Address selection for INSTRUCTOR -->
          <template v-if="editDialog.mode === 'create' && editDialog.form.role === 'INSTRUCTOR'">
            <div class="address-row">
              <v-select
                v-model="editDialog.form.addressId"
                :items="allAddresses"
                :label="$t('adminUsers.address') || 'Адрес'"
                item-title="address"
                item-value="id"
                prepend-inner-icon="mdi-map-marker"
                clearable
                density="compact"
                hide-details
                class="address-select"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.address" :subtitle="item.raw.city || ''" />
                </template>
              </v-select>
              <v-btn
                icon="mdi-plus"
                color="primary"
                variant="tonal"
                class="address-add-btn"
                @click="inlineAddress.visible = true"
                :title="$t('adminUsers.addNewAddress') || 'Жаңа мекенжай қосу'"
              />
            </div>

            <v-expand-transition>
              <v-card v-if="inlineAddress.visible" variant="outlined" class="mb-3 pa-3">
                <div class="text-subtitle-2 mb-2">
                  <v-icon size="small" class="me-1">mdi-map-marker-plus</v-icon>
                  {{ $t('adminUsers.addNewAddress') || 'Жаңа мекенжай қосу' }}
                </div>
                <v-text-field
                  v-model="inlineAddress.name"
                  :label="$t('adminUsers.addressName') || 'Атауы (мысалы: Абай филиалы)'"
                  density="compact"
                  hide-details
                  class="mb-2"
                />
                <v-text-field
                  v-model="inlineAddress.address"
                  :label="$t('adminUsers.addressFull') || 'Толық мекенжай'"
                  density="compact"
                  hide-details
                  class="mb-2"
                />
                <v-text-field
                  v-model="inlineAddress.city"
                  :label="$t('adminUsers.addressCity') || 'Қала'"
                  density="compact"
                  hide-details
                  class="mb-2"
                />
                <div class="d-flex justify-end ga-2">
                  <v-btn variant="text" size="small" @click="closeInlineAddress">
                    {{ $t('common.cancel') }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    size="small"
                    :loading="inlineAddress.loading"
                    :disabled="!inlineAddress.name || !inlineAddress.address"
                    @click="createInlineAddress"
                  >
                    {{ $t('common.save') }}
                  </v-btn>
                </div>
              </v-card>
            </v-expand-transition>
          </template>

          <!-- Instructor selection for STUDENT/LIMITED_STUDENT (optional) -->
          <v-select
            v-if="editDialog.mode === 'create' && ['STUDENT', 'LIMITED_STUDENT'].includes(editDialog.form.role)"
            v-model="editDialog.form.instructorId"
            :items="instructorOptions"
            :label="$t('adminUsers.assignedInstructor') + ' (' + $t('common.optional') + ')'"
            item-title="title"
            item-value="value"
            prepend-inner-icon="mdi-account-tie"
            clearable
            density="compact"
          />

          <v-select
            v-model="editDialog.form.status"
            :items="statusItems"
            :label="$t('common.status')"
            item-title="title"
            item-value="value"
          />
          
          <!-- Expiry Date -->
          <v-divider class="my-4" />
          <div class="text-subtitle-2 mb-2">{{ $t('adminUsers.expiryDateLabel') }}</div>
          <v-checkbox
            v-model="editDialog.form.unlimitedAccess"
            :label="$t('adminUsers.unlimited')"
            density="compact"
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-if="!editDialog.form.unlimitedAccess"
            v-model="editDialog.form.expiryDate"
            type="date"
            :label="$t('adminUsers.expiryDateLabel')"
            :hint="$t('adminUsers.expiryHint')"
            persistent-hint
            prepend-inner-icon="mdi-calendar"
            variant="outlined"
            density="compact"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeEditDialog">{{ $t('common.close') }}</v-btn>
          <v-btn color="primary" :loading="editDialog.loading" @click="saveUser">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteDialog.visible" max-width="420">
      <v-card>
        <v-card-title>{{ $t('common.delete') }}</v-card-title>
        <v-card-text>
          {{ $t('adminUsers.deleteConfirm', { name: deleteDialog.user?.fio || '—' }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog.visible = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" :loading="deleteDialog.loading" @click="doDelete">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Password Dialog -->
    <v-dialog v-model="passwordDialog.visible" max-width="480">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="orange" class="me-2">mdi-lock</v-icon>
          {{ $t('auth.changePassword') }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-4">
            {{ $t('adminUsers.changePasswordFor') }} <strong>{{ passwordDialog.user?.fio }}</strong>
          </v-alert>
          
          <v-text-field
            v-model="passwordDialog.newPassword"
            :label="$t('auth.newPassword')"
            :type="showNewPassword ? 'text' : 'password'"
            prepend-inner-icon="mdi-lock"
            :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showNewPassword = !showNewPassword"
            variant="outlined"
            :rules="[v => !!v || $t('validation.required'), v => v.length >= 6 || '6+']"
            :hint="$t('adminUsers.minChars')"
            persistent-hint
            class="mb-4"
          />
          
          <v-text-field
            v-model="passwordDialog.confirmPassword"
            :label="$t('auth.confirmPassword')"
            :type="showConfirmPassword ? 'text' : 'password'"
            prepend-inner-icon="mdi-lock-check"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
            variant="outlined"
            :rules="[v => !!v || $t('validation.required'), v => v === passwordDialog.newPassword || $t('validation.passwordMatch')]"
            :hint="$t('adminUsers.repeatPassword')"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closePasswordDialog">{{ $t('common.close') }}</v-btn>
          <v-btn 
            color="orange" 
            :loading="passwordDialog.loading" 
            :disabled="!passwordDialog.newPassword || passwordDialog.newPassword !== passwordDialog.confirmPassword || passwordDialog.newPassword.length < 6"
            @click="changePassword"
          >
            {{ $t('auth.changePassword') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    

    <!-- Expiry Date Dialog -->
    <v-dialog v-model="expiryDialog.visible" max-width="480">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="info" class="me-2">mdi-calendar-clock</v-icon>
          {{ $t('adminUsers.expiryDialogTitle') }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-4">
            {{ $t('adminUsers.expiryDialogSubtitle') }} <strong>{{ expiryDialog.user?.fio }}</strong>
          </v-alert>
          
          <v-checkbox
            v-model="expiryDialog.removeExpiry"
            :label="$t('adminUsers.removeExpiry')"
            color="primary"
            class="mb-4"
          />
          
          <v-text-field
            v-if="!expiryDialog.removeExpiry"
            v-model="expiryDialog.expiryDate"
            :label="$t('adminUsers.expiryDateLabel')"
            type="date"
            prepend-inner-icon="mdi-calendar"
            variant="outlined"
            :rules="[v => expiryDialog.removeExpiry || !!v || $t('validation.required')]"
            :hint="$t('adminUsers.expiryHint')"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeExpiryDialog">{{ $t('common.close') }}</v-btn>
          <v-btn 
            color="info" 
            :loading="expiryDialog.loading"
            @click="updateExpiry"
          >
            {{ expiryDialog.removeExpiry ? $t('adminUsers.removeExpiryAction') : $t('adminUsers.setExpiryAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Role Change Confirm Dialog -->
    <v-dialog v-model="roleDialog.visible" max-width="400">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="warning" class="me-2">mdi-shield-account</v-icon>
          {{ $t('adminUsers.changeRoleTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('adminUsers.changeRoleConfirm', { name: roleDialog.user?.fio || '', role: getRoleTitle(roleDialog.newRole) }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelRoleChange">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="warning" variant="flat" :loading="roleDialog.loading" @click="applyRoleChange">{{ $t('common.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Actions Dialog -->
    <v-dialog v-model="actionsDialog.visible" max-width="360">
      <v-card v-if="actionsDialog.user">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" size="small">mdi-account-cog</v-icon>
          <span class="text-truncate">{{ actionsDialog.user.fio || '#' + actionsDialog.user.id }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="actionsDialog.visible = false" />
        </v-card-title>
        <v-divider />
        <v-list density="compact">
          <v-list-item 
            prepend-icon="mdi-pencil" 
            :title="$t('adminUsers.editContact')"
            @click="actionsDialog.visible = false; openEditDialog(actionsDialog.user)"
          />
          <v-list-item 
            prepend-icon="mdi-lock" 
            :title="$t('auth.changePassword')"
            @click="actionsDialog.visible = false; openPasswordDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="orange">mdi-lock</v-icon>
            </template>
          </v-list-item>
          <v-list-item 
            prepend-icon="mdi-calendar-clock" 
            :title="$t('adminUsers.setOrChangeExpiry')"
            @click="actionsDialog.visible = false; openExpiryDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="info">mdi-calendar-clock</v-icon>
            </template>
          </v-list-item>
          <v-list-item 
            prepend-icon="mdi-lock-reset" 
            :title="$t('adminUsers.sendTempPassword')"
            @click="actionsDialog.visible = false; sendTempPassword(actionsDialog.user)"
          />
          <v-list-item
            prepend-icon="mdi-refresh-circle"
            :title="$t('adminUsers.forceRefresh')"
            :disabled="!actionsDialog.user?.isOnline"
            @click="actionsDialog.visible = false; sendForceRefresh(actionsDialog.user)"
          />
          <v-list-item
            prepend-icon="mdi-telegram"
            title="Telegram код жасау"
            subtitle="Оқушы/инструктор ботқа осы кодты жібереді"
            @click="actionsDialog.visible = false; generateTelegramCode(actionsDialog.user)"
          />
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.assignAddress')"
            @click="actionsDialog.visible = false; openAddressDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="teal">mdi-map-marker</v-icon>
            </template>
            <template #subtitle>
              <span v-if="instructorAddresses(actionsDialog.user).length" class="text-caption">
                {{ formatInstructorAddresses(actionsDialog.user) }}
              </span>
            </template>
          </v-list-item>
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.editInstructorProfile') || 'Анкета толтыру'"
            :subtitle="$t('adminUsers.editInstructorProfileSubtitle') || 'Фото, көлік, бос уақыттар'"
            @click="actionsDialog.visible = false; openInstructorProfileDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="primary">mdi-card-account-details-outline</v-icon>
            </template>
          </v-list-item>
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.manageSchedule') || 'Кесте басқару'"
            :subtitle="$t('adminUsers.manageScheduleSubtitle') || 'Брондалған сабақтарды қарау, өзгерту'"
            @click="actionsDialog.visible = false; openScheduleDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="indigo">mdi-calendar-clock</v-icon>
            </template>
          </v-list-item>
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.viewAllSchedulesPermission')"
            :subtitle="actionsDialog.user?.canViewAllSchedules ? $t('adminUsers.permissionEnabled') : $t('adminUsers.permissionDisabled')"
            @click.stop
          >
            <template #prepend>
              <v-icon :color="actionsDialog.user?.canViewAllSchedules ? 'success' : 'grey'">mdi-calendar-edit</v-icon>
            </template>
            <template #append>
              <v-switch
                :model-value="!!actionsDialog.user?.canViewAllSchedules"
                color="success"
                density="compact"
                hide-details
                inset
                @update:model-value="toggleAllSchedulesPermission(actionsDialog.user, $event)"
              />
            </template>
          </v-list-item>
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.assignStudents') || 'Оқушыларды тағайындау'"
            :subtitle="$t('adminUsers.assignStudentsSubtitle') || 'Бірнеше оқушыны бір уақытта таңдау'"
            @click="actionsDialog.visible = false; openAssignStudentsDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="teal">mdi-account-multiple-plus</v-icon>
            </template>
          </v-list-item>
          <v-list-item
            v-if="actionsDialog.user?.role === 'INSTRUCTOR'"
            :title="$t('adminUsers.assignTests') || 'Тест тағайындау'"
            :subtitle="$t('adminUsers.assignTestsSubtitle') || 'Топтың барлық оқушысына'"
            @click="actionsDialog.visible = false; openAssignTestsDialog(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="deep-purple">mdi-clipboard-list-outline</v-icon>
            </template>
          </v-list-item>
          <v-list-item
            v-if="['STUDENT', 'LIMITED_STUDENT'].includes(actionsDialog.user?.role)"
            :title="$t('adminUsers.resetIncorrect') || 'Қателерді тазалау'"
            :subtitle="$t('adminUsers.resetIncorrectSubtitle') || 'Қатемен жұмыс тізімін бос қылады'"
            @click="actionsDialog.visible = false; resetIncorrectQuestions(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="warning">mdi-eraser</v-icon>
            </template>
          </v-list-item>
          <v-divider />
          <v-list-item 
            v-if="!actionsDialog.confirmingDelete"
            prepend-icon="mdi-delete" 
            :title="$t('common.delete')"
            base-color="error"
            @click="actionsDialog.confirmingDelete = true"
          />
          <v-list-item 
            v-else
            base-color="error"
            @click="actionsDialog.visible = false; actionsDialog.confirmingDelete = false; confirmDelete(actionsDialog.user)"
          >
            <template #prepend>
              <v-icon color="error">mdi-alert</v-icon>
            </template>
            <v-list-item-title class="font-weight-bold text-error">
              {{ $t('adminUsers.confirmDeleteShort') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <!-- Telegram Link Dialog -->
    <v-dialog v-model="telegramDialog.visible" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="me-2" color="#229ED9">mdi-telegram</v-icon>
          Telegram ботқа қосу
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="telegramDialog.visible = false" />
        </v-card-title>
        <v-card-subtitle v-if="telegramDialog.user" class="pb-2">
          {{ telegramDialog.user.fio }} · {{ telegramDialog.user.phone }}
        </v-card-subtitle>
        <v-divider />
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Оқушы/инструктор сілтемені ашса, Telegram бот кодпен бірге тіркеуді бастайды.
            Ашылмаса, ботқа төмендегі команданы қолмен жібереді.
          </v-alert>

          <div class="telegram-link-block mb-3">
            <div class="text-caption text-medium-emphasis mb-1">Толық сілтеме</div>
            <div class="telegram-link-row">
              <code>{{ telegramDialog.link }}</code>
              <v-btn icon="mdi-content-copy" size="small" variant="text" @click="copyTelegramValue(telegramDialog.link)" />
              <v-btn icon="mdi-open-in-new" size="small" variant="text" color="primary" @click="openTelegramLink" />
            </div>
          </div>

          <div class="telegram-link-block mb-3">
            <div class="text-caption text-medium-emphasis mb-1">Команда</div>
            <div class="telegram-link-row">
              <code>{{ telegramDialog.command }}</code>
              <v-btn icon="mdi-content-copy" size="small" variant="text" @click="copyTelegramValue(telegramDialog.command)" />
            </div>
          </div>

          <div class="telegram-link-block">
            <div class="text-caption text-medium-emphasis mb-1">Код</div>
            <div class="telegram-link-row">
              <code>{{ telegramDialog.code }}</code>
              <v-btn icon="mdi-content-copy" size="small" variant="text" @click="copyTelegramValue(telegramDialog.code)" />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="flat" @click="copyTelegramValue(telegramShareText)">
            <v-icon start>mdi-content-copy</v-icon>
            Барлығын көшіру
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="telegramDialog.visible = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Address Assignment Dialog -->
    <v-dialog v-model="addressDialog.visible" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="me-2" color="teal">mdi-map-marker</v-icon>
          {{ $t('adminUsers.assignAddress') }}
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="addressDialog.visible = false" />
        </v-card-title>
        <v-card-subtitle v-if="addressDialog.user" class="pb-2">
          {{ addressDialog.user.fio }}
        </v-card-subtitle>
        <v-divider />
        <v-card-text>
          <!-- Бар мекенжайды таңдау -->
          <v-select
            v-model="addressDialog.selectedAddressIds"
            :items="addressOptions"
            item-title="label"
            item-value="value"
            :label="$t('adminUsers.selectAddress')"
            multiple
            chips
            closable-chips
            clearable
            variant="outlined"
            density="comfortable"
            :no-data-text="$t('adminUsers.noAddresses')"
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #subtitle v-if="item.raw.subtitle">
                  {{ item.raw.subtitle }}
                </template>
              </v-list-item>
            </template>
          </v-select>

          <v-divider class="my-3" />

          <!-- Жаңа мекенжай қосу -->
          <div class="text-subtitle-2 mb-2">{{ $t('adminUsers.addNewAddress') }}</div>
          <v-text-field
            v-model="addressDialog.newName"
            :label="$t('adminUsers.addressName')"
            :placeholder="$t('adminUsers.addressNamePlaceholder')"
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <v-text-field
            v-model="addressDialog.newAddress"
            :label="$t('common.address')"
            :placeholder="$t('adminUsers.addressPlaceholder')"
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <v-text-field
            v-model="addressDialog.newCity"
            :label="$t('adminUsers.city')"
            variant="outlined"
            density="compact"
            class="mb-2"
          />
          <v-btn
            color="teal"
            variant="tonal"
            size="small"
            :disabled="!addressDialog.newName || !addressDialog.newAddress"
            :loading="addressDialog.creating"
            @click="createNewAddress"
            prepend-icon="mdi-plus"
          >
            {{ $t('adminUsers.createAddress') }}
          </v-btn>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="addressDialog.visible = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="teal" :loading="addressDialog.loading" @click="saveInstructorAddress">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Instructor Profile Editor Dialog -->
    <v-dialog v-model="instructorProfileDialog.visible" max-width="640" scrollable>
      <v-card v-if="instructorProfileDialog.form">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" color="primary">mdi-card-account-details-outline</v-icon>
          <span class="text-truncate">
            {{ $t('adminUsers.editInstructorProfile') || 'Анкета толтыру' }}
            — {{ instructorProfileDialog.user?.fio }}
          </span>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="instructorProfileDialog.visible = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height: 70vh;">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption mb-1">{{ $t('instructor.yourPhoto') || 'Фотосы' }}</div>
              <div class="profile-photo-box mb-2">
                <v-img
                  v-if="instructorProfileDialog.photoPreview"
                  :src="instructorProfileDialog.photoPreview"
                  height="150"
                  contain
                />
                <v-icon v-else size="60" color="grey">mdi-account-circle</v-icon>
              </div>
              <v-file-input
                v-model="instructorProfileDialog.photoFile"
                accept="image/*"
                density="compact"
                hide-details
                prepend-icon="mdi-camera"
                :label="$t('common.upload') || 'Жүктеу'"
                @change="onProfilePhotoSelect('photo')"
                :loading="instructorProfileDialog.uploadingPhoto"
              />
            </v-col>
            <v-col cols="6">
              <div class="text-caption mb-1">{{ $t('instructor.carPhoto') || 'Көлік суреті' }}</div>
              <div class="profile-photo-box mb-2">
                <v-img
                  v-if="instructorProfileDialog.carPhotoPreview"
                  :src="instructorProfileDialog.carPhotoPreview"
                  height="150"
                  contain
                />
                <v-icon v-else size="60" color="grey">mdi-car</v-icon>
              </div>
              <v-file-input
                v-model="instructorProfileDialog.carPhotoFile"
                accept="image/*"
                density="compact"
                hide-details
                prepend-icon="mdi-car"
                :label="$t('common.upload') || 'Жүктеу'"
                @change="onProfilePhotoSelect('carPhoto')"
                :loading="instructorProfileDialog.uploadingCarPhoto"
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="instructorProfileDialog.form.carModel"
            :label="$t('instructor.carModel') || 'Көлік моделі'"
            prepend-inner-icon="mdi-car-info"
            density="compact"
            class="mt-4"
          />
          <v-text-field
            v-model="instructorProfileDialog.form.carNumber"
            :label="$t('instructor.carNumber') || 'Мемлекеттік нөмір / Госномер'"
            prepend-inner-icon="mdi-card-text-outline"
            density="compact"
            maxlength="20"
            @update:model-value="instructorProfileDialog.form.carNumber = String($event || '').toUpperCase()"
          />
          <v-textarea
            v-model="instructorProfileDialog.form.description"
            :label="$t('common.description') || 'Сипаттама'"
            prepend-inner-icon="mdi-text"
            density="compact"
            rows="2"
            auto-grow
          />
          <v-select
            v-model="instructorProfileDialog.form.freeDays"
            :items="dayOptions"
            :label="$t('student.instructors.freeDays') || 'Жұмыс күндері'"
            item-title="title"
            item-value="value"
            multiple
            chips
            closable-chips
            density="compact"
            prepend-inner-icon="mdi-calendar"
          />
          <v-select
            v-model="instructorProfileDialog.form.freeTime"
            :items="timeSlotOptions"
            :label="$t('student.instructors.freeTime') || 'Жұмыс уақыты'"
            multiple
            chips
            closable-chips
            density="compact"
            prepend-inner-icon="mdi-clock"
          />

          <v-select
            v-model="instructorProfileDialog.form.addressIds"
            :items="allAddresses"
            :label="$t('adminUsers.address') || 'Мекенжайлар'"
            item-title="address"
            item-value="id"
            multiple
            chips
            closable-chips
            density="compact"
            prepend-inner-icon="mdi-map-marker-multiple"
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw.address" :subtitle="item.raw.city || ''" />
            </template>
          </v-select>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="instructorProfileDialog.visible = false">
            {{ $t('common.close') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="instructorProfileDialog.saving"
            @click="saveInstructorProfile"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Schedule Management Dialog -->
    <v-dialog v-model="scheduleDialog.visible" max-width="900" scrollable>
      <v-card v-if="scheduleDialog.user">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" color="indigo">mdi-calendar-clock</v-icon>
          <span class="text-truncate">
            {{ $t('adminUsers.manageSchedule') || 'Кесте басқару' }} —
            {{ scheduleDialog.user.fio }}
          </span>
          <v-spacer />
          <v-btn icon="mdi-refresh" size="small" variant="text" :loading="scheduleDialog.loading" @click="loadInstructorSchedule" />
          <v-btn icon="mdi-close" size="small" variant="text" @click="scheduleDialog.visible = false" />
        </v-card-title>
        <v-divider />

        <!-- Date range filter -->
        <div class="d-flex align-center ga-2 pa-3 flex-wrap">
          <v-text-field
            v-model="scheduleDialog.startDate"
            type="date"
            :label="$t('common.from') || 'Бастап'"
            density="compact"
            hide-details
            style="max-width: 180px;"
            @change="loadInstructorSchedule"
          />
          <v-text-field
            v-model="scheduleDialog.endDate"
            type="date"
            :label="$t('common.to') || 'Дейін'"
            density="compact"
            hide-details
            style="max-width: 180px;"
            @change="loadInstructorSchedule"
          />
          <v-select
            v-model="scheduleDialog.statusFilter"
            :items="scheduleStatusItems"
            density="compact"
            hide-details
            style="max-width: 200px;"
            :label="$t('common.status') || 'Статус'"
          />
          <v-spacer />
          <v-btn color="primary" size="small" prepend-icon="mdi-plus" @click="openAddBookingForm">
            {{ $t('adminUsers.addBooking') || 'Сабақ қосу' }}
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="pa-0" style="max-height: 60vh;">
          <!-- Quick add form -->
          <v-expand-transition>
            <div v-if="scheduleDialog.addForm.visible" class="pa-4 add-booking-form">
              <div class="text-subtitle-2 mb-2">{{ $t('adminUsers.addBooking') || 'Сабақ қосу' }}</div>
              <div class="d-flex flex-wrap ga-2">
                <v-select
                  v-model="scheduleDialog.addForm.studentId"
                  :items="scheduleDialog.studentOptions"
                  item-title="title"
                  item-value="value"
                  :label="$t('common.student') || 'Оқушы'"
                  density="compact"
                  hide-details
                  style="min-width: 200px;"
                />
                <v-text-field
                  v-model="scheduleDialog.addForm.date"
                  type="date"
                  :label="$t('common.date') || 'Күні'"
                  density="compact"
                  hide-details
                  style="max-width: 170px;"
                />
                <v-select
                  v-model="scheduleDialog.addForm.timeSlot"
                  :items="instructorTimeSlots"
                  :label="$t('common.time') || 'Уақыты'"
                  density="compact"
                  hide-details
                  style="min-width: 180px;"
                />
                <v-btn
                  color="success"
                  size="small"
                  :loading="scheduleDialog.addForm.saving"
                  :disabled="!scheduleDialog.addForm.studentId || !scheduleDialog.addForm.date || !scheduleDialog.addForm.timeSlot"
                  @click="createBooking"
                >
                  {{ $t('common.save') }}
                </v-btn>
                <v-btn size="small" variant="text" @click="scheduleDialog.addForm.visible = false">
                  {{ $t('common.cancel') }}
                </v-btn>
              </div>
            </div>
          </v-expand-transition>

          <v-table density="compact" v-if="filteredSchedules.length" hover>
            <thead>
              <tr>
                <th>{{ $t('common.date') || 'Күні' }}</th>
                <th>{{ $t('common.time') || 'Уақыты' }}</th>
                <th>{{ $t('common.student') || 'Оқушы' }}</th>
                <th>{{ $t('common.status') || 'Статус' }}</th>
                <th class="text-right">{{ $t('common.actions') || 'Әрекет' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in filteredSchedules" :key="s.id">
                <td>{{ formatScheduleDate(s.date) }}</td>
                <td class="font-weight-medium">{{ s.timeSlot }}</td>
                <td>
                  <div>{{ s.student?.fio || '—' }}</div>
                  <div v-if="s.student?.phone" class="text-caption text-medium-emphasis">{{ s.student.phone }}</div>
                </td>
                <td>
                  <v-chip :color="getScheduleStatusColor(s.status)" size="x-small" variant="flat">
                    {{ s.status }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <v-tooltip text="+15 мин">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="x-small" variant="text" icon="mdi-clock-plus" @click="shiftBookingBy15(s)" />
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Болдырмау">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        size="x-small"
                        variant="text"
                        color="error"
                        icon="mdi-close-circle"
                        :disabled="s.status === 'CANCELLED'"
                        @click="cancelBooking(s)"
                      />
                    </template>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-else-if="!scheduleDialog.loading" class="text-center pa-8 text-medium-emphasis">
            <v-icon size="48" color="grey-lighten-1">mdi-calendar-blank</v-icon>
            <div class="mt-2">{{ $t('adminUsers.noBookings') || 'Сабақтар табылмады' }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bulk Assign Tests Dialog -->
    <v-dialog v-model="assignTestsDialog.visible" max-width="780" scrollable>
      <v-card v-if="assignTestsDialog.instructor">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" color="deep-purple">mdi-clipboard-list-outline</v-icon>
          <span class="text-truncate">
            {{ $t('adminUsers.assignTests') || 'Тест тағайындау' }} —
            {{ assignTestsDialog.instructor.fio }}
          </span>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="assignTestsDialog.visible = false" />
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4" style="max-height: 65vh;">
          <v-text-field
            v-model="assignTestsDialog.title"
            :label="$t('adminAnalytics.assignTitle') || 'Тағайындау атауы'"
            density="compact"
            class="mb-3"
          />

          <!-- Topic picker -->
          <div class="mb-3">
            <div class="text-subtitle-2 mb-2">
              <v-icon size="small" class="me-1">mdi-tag-multiple</v-icon>
              {{ $t('adminUsers.byTopic') || 'Тақырып бойынша' }}
            </div>
            <v-select
              v-model="assignTestsDialog.selectedTopics"
              :items="assignTestsDialog.topics"
              :label="$t('adminUsers.selectTopics') || 'Тақырыптарды таңдаңыз'"
              multiple
              chips
              closable-chips
              density="compact"
              clearable
              :loading="assignTestsDialog.loadingTopics"
              @update:model-value="onTopicChange"
            />
          </div>

          <div class="text-caption text-medium-emphasis mb-3">
            {{ $t('adminUsers.testsSelected') || 'Сұрақтар таңдалды' }}:
            <strong>{{ assignTestsDialog.selectedTestIds.length }}</strong>
          </div>

          <v-divider class="mb-3" />

          <!-- Students -->
          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-2">
              <v-icon size="small" class="me-1">mdi-account-group</v-icon>
              {{ $t('adminUsers.targetStudents') || 'Оқушылар' }}
              ({{ assignTestsDialog.studentSelection.size }}/{{ assignTestsDialog.students.length }})
            </div>
            <v-spacer />
            <v-btn size="x-small" variant="text" @click="toggleAllAssignTestStudents">
              {{ allStudentsChecked ? ($t('common.clear') || 'Тазалау') : ($t('adminUsers.selectAll') || 'Барлығын') }}
            </v-btn>
          </div>

          <v-list density="compact" max-height="200" class="border rounded">
            <v-list-item
              v-for="s in assignTestsDialog.students"
              :key="s.id"
              @click="toggleAssignTestStudent(s.id)"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="assignTestsDialog.studentSelection.has(s.id)"
                  density="compact"
                  hide-details
                  @click.stop="toggleAssignTestStudent(s.id)"
                />
              </template>
              <v-list-item-title>{{ s.fio }}</v-list-item-title>
              <v-list-item-subtitle>{{ s.phone }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="!assignTestsDialog.students.length">
              <v-list-item-title class="text-center text-medium-emphasis py-4">
                {{ $t('adminUsers.noStudentsAssigned') || 'Бұл инструкторға студент тіркелмеген' }}
              </v-list-item-title>
            </v-list-item>
          </v-list>

          <v-textarea
            v-model="assignTestsDialog.notes"
            :label="$t('common.notes') || 'Ескертпе'"
            density="compact"
            rows="2"
            class="mt-3"
          />
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="text" @click="assignTestsDialog.visible = false">{{ $t('common.close') }}</v-btn>
          <v-btn
            color="primary"
            :loading="assignTestsDialog.saving"
            :disabled="!assignTestsDialog.selectedTestIds.length || !assignTestsDialog.studentSelection.size"
            @click="submitBulkTestAssignment"
          >
            {{ $t('common.save') }}
            ({{ assignTestsDialog.selectedTestIds.length }} × {{ assignTestsDialog.studentSelection.size }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Assign Students Dialog -->
    <v-dialog v-model="assignStudentsDialog.visible" max-width="720" scrollable>
      <v-card v-if="assignStudentsDialog.instructor">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" color="teal">mdi-account-multiple-plus</v-icon>
          <span class="text-truncate">
            {{ $t('adminUsers.assignStudents') || 'Оқушыларды тағайындау' }} —
            {{ assignStudentsDialog.instructor.fio }}
          </span>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="assignStudentsDialog.visible = false" />
        </v-card-title>
        <v-divider />

        <div class="pa-3 d-flex align-center ga-2 flex-wrap">
          <v-text-field
            v-model="assignStudentsDialog.search"
            :placeholder="$t('common.search') || 'Іздеу'"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            style="min-width: 220px; flex: 1;"
          />
          <v-select
            v-model="assignStudentsDialog.filter"
            :items="assignFilterOptions"
            density="compact"
            hide-details
            style="max-width: 200px;"
          />
          <v-chip color="primary" size="small" variant="tonal">
            {{ $t('adminUsers.selected') || 'Таңдалды' }}: {{ assignStudentsDialog.selected.size }}
          </v-chip>
        </div>

        <v-divider />

        <v-card-text class="pa-0" style="max-height: 55vh;">
          <div v-if="assignStudentsDialog.loading" class="text-center pa-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-list v-else density="compact" lines="two">
            <v-list-item
              v-for="student in filteredAssignStudents"
              :key="student.id"
              @click="toggleStudentSelect(student.id)"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="assignStudentsDialog.selected.has(student.id)"
                  color="primary"
                  density="compact"
                  hide-details
                  @click.stop="toggleStudentSelect(student.id)"
                />
              </template>
              <v-list-item-title>
                {{ student.fio }}
                <v-chip
                  v-if="student.instructorId === assignStudentsDialog.instructor.instructor?.id"
                  size="x-small"
                  color="success"
                  variant="tonal"
                  class="ms-2"
                >
                  {{ $t('adminUsers.currentlyAssigned') || 'Тағайындалған' }}
                </v-chip>
                <v-chip
                  v-else-if="student.instructor?.fio"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  class="ms-2"
                >
                  → {{ student.instructor.fio }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ student.phone }}
                <span v-if="student.role === 'LIMITED_STUDENT'" class="text-warning">
                  · LIMITED
                </span>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="!filteredAssignStudents.length">
              <v-list-item-title class="text-center text-medium-emphasis py-4">
                {{ $t('adminUsers.noStudentsFound') || 'Оқушы табылмады' }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn variant="text" @click="selectAllFilteredStudents">
            {{ $t('adminUsers.selectAll') || 'Барлығын таңдау' }}
          </v-btn>
          <v-btn variant="text" @click="assignStudentsDialog.selected.clear()">
            {{ $t('common.clear') || 'Тазалау' }}
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="assignStudentsDialog.visible = false">{{ $t('common.close') }}</v-btn>
          <v-btn
            color="primary"
            :loading="assignStudentsDialog.saving"
            :disabled="!assignStudentsDialog.selected.size"
            @click="saveStudentAssignments"
          >
            {{ $t('common.save') }} ({{ assignStudentsDialog.selected.size }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rounds Statistics Dialog -->
    <v-dialog v-model="roundsDialog.visible" max-width="850">
      <v-card v-if="roundsDialog.user">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon class="me-2" size="small">mdi-chart-timeline-variant</v-icon>
          <span class="text-truncate">{{ roundsDialog.user.fio || '#' + roundsDialog.user.id }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="roundsDialog.visible = false" />
        </v-card-title>
        <v-card-subtitle>{{ $t('adminUsers.rounds.title') }}</v-card-subtitle>
        <v-divider />
        <v-card-text class="pa-0">
          <v-table density="compact" v-if="roundsDialog.user.testStats?.rounds?.length">
            <thead>
              <tr>
                <th>{{ $t('adminUsers.rounds.round') }}</th>
                <th class="text-success">{{ $t('adminUsers.rounds.correct') }}</th>
                <th class="text-error">{{ $t('adminUsers.rounds.incorrect') }}</th>
                <th class="text-info">{{ $t('adminUsers.rounds.accuracy') }}</th>
                <th>{{ $t('adminUsers.rounds.progress') }}</th>
                <th>{{ $t('adminUsers.rounds.date') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in roundsDialog.user.testStats.rounds" :key="r.round" :style="r.round === roundsDialog.user.testStats.round ? 'background: rgba(33, 150, 243, 0.15)' : ''">
                <td class="font-weight-bold">
                  {{ r.round }}
                  <v-chip v-if="r.round === roundsDialog.user.testStats.round" size="x-small" color="info" variant="flat" class="ml-1">
                    {{ $t('adminUsers.rounds.current') }}
                  </v-chip>
                </td>
                <td class="text-success font-weight-bold">{{ r.correct }}</td>
                <td class="text-error font-weight-bold">{{ r.incorrect }}</td>
                <td class="text-info font-weight-bold">{{ r.percentage }}%</td>
                <td>
                  <div class="d-flex align-center gap-2">
                    <v-progress-linear :model-value="r.progress" :color="r.progress >= 100 ? 'success' : 'primary'" height="6" rounded style="min-width: 60px; max-width: 100px;" />
                    <span class="text-caption">{{ r.answered }}/{{ r.totalTests }} ({{ r.progress }}%)</span>
                  </div>
                </td>
                <td class="text-caption text-no-wrap">
                  <div v-if="r.firstDate">{{ formatShortDate(r.firstDate) }}</div>
                  <div v-if="r.lastDate && formatShortDate(r.lastDate) !== formatShortDate(r.firstDate)" class="text-medium-emphasis">{{ formatShortDate(r.lastDate) }}</div>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-else class="pa-4 text-center text-medium-emphasis">
            {{ $t('adminUsers.rounds.noData') }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Phone conflicts (duplicates) dialog -->
    <v-dialog v-model="phoneConflictsDialog" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="warning" class="me-2">mdi-alert-circle</v-icon>
          {{ $t('adminUsers.fixPhonesConflictsTitle') }}
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="phoneConflictsDialog = false" />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <p class="text-body-2 text-medium-emphasis mb-3">{{ $t('adminUsers.fixPhonesConflictsHint') }}</p>
          <v-list density="compact">
            <v-list-item v-for="c in phoneConflicts" :key="c.id" class="px-2">
              <v-list-item-title class="text-body-2">
                <code>{{ c.phone }}</code> → <code>{{ c.normalized }}</code>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ c.fio || ('#' + c.id) }}
                <span v-if="c.dupFio || c.dupId"> · {{ $t('adminUsers.fixPhonesDuplicateOf') }}: {{ c.dupFio || ('#' + c.dupId) }}</span>
              </v-list-item-subtitle>
              <template #append>
                <v-btn color="error" variant="tonal" size="small" prepend-icon="mdi-delete" @click="deleteConflictUser(c)">
                  {{ $t('adminUsers.delete') || $t('common.delete') }}
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="phoneConflictsDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import { useSnackbarStore } from '@/stores/snackbar'
import { useSocketStore } from '@/stores/socket'
import PhoneInput from '@/components/ui/PhoneInput.vue'

const snackbar = useSnackbarStore()
const socketStore = useSocketStore()

const { t } = useI18n()

const headers = [
  { title: 'ID', value: 'id', width: 70, sortable: true },
  { title: '🟢', value: 'isOnline', width: 60, sortable: true },
  { title: t('common.fullName'), value: 'fio', sortable: true },
  { title: t('common.phone'), value: 'phone', sortable: false },
  { title: t('adminUsers.version'), value: 'appVersion', width: 100, sortable: false },
  { title: '📊 ' + t('adminUsers.testStats'), value: 'testStats', width: 120, sortable: false },
  { title: 'Email', value: 'email', sortable: false },
  { title: t('admin.userRole'), value: 'role', width: 150, sortable: true },
  { title: t('common.status'), value: 'status', width: 140, sortable: true },
  { title: t('adminUsers.expiry'), value: 'expiryDate', width: 180, sortable: true },
  { title: t('adminUsers.createdBy'), value: 'creator', width: 180, sortable: false },
  { title: t('adminUsers.updatedBy'), value: 'updater', width: 180, sortable: false },
  { title: t('adminUsers.loginLogout'), value: 'lastSession', width: 160, sortable: true },
  { title: '', value: 'actions', sortable: false, width: 60 }
]

// Vuetify column key → backend sortBy mapping
const sortKeyMap = {
  id: 'id',
  isOnline: 'online',
  fio: 'fio',
  role: 'role',
  status: 'status',
  expiryDate: 'expiryDate',
  lastSession: 'lastLogin'
}

const tableSortBy = ref([{ key: 'isOnline', order: 'desc' }])

const roleItems = computed(() => ([
  { title: t('roles.ADMIN'), value: 'ADMIN' },
  { title: t('roles.ADMIN_LIMITED'), value: 'ADMIN_LIMITED' },
  { title: t('roles.STUDENT'), value: 'STUDENT' },
  { title: t('roles.LIMITED_STUDENT'), value: 'LIMITED_STUDENT' },
  { title: t('roles.INSTRUCTOR'), value: 'INSTRUCTOR' },
  { title: t('roles.THEORY_TEACHER'), value: 'THEORY_TEACHER' }
]))

const statusItems = computed(() => ([
  { title: t('status.ACTIVE'), value: 'ACTIVE' },
  { title: t('status.PENDING'), value: 'PENDING' },
  { title: t('status.BLOCKED'), value: 'BLOCKED' }
]))

const users = ref([])
const loading = ref(false)
const onlineCount = ref(0)
const filters = ref({ search: '', role: null, status: null, sortBy: 'online', sortOrder: 'desc' })
const pagination = ref({ page: 1, limit: 10, total: 0 })
const totalPages = computed(() => Math.max(1, Math.ceil(pagination.value.total / pagination.value.limit)))

const editDialog = ref({ visible: false, mode: 'create', loading: false, form: { id: null, fio: '', phone: '', email: '', password: '', role: 'STUDENT', status: 'ACTIVE' } })
const showPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const deleteDialog = ref({ visible: false, loading: false, user: null })
const passwordDialog = ref({ visible: false, loading: false, user: null, newPassword: '', confirmPassword: '' })
const expiryDialog = ref({ visible: false, loading: false, user: null, expiryDate: null, removeExpiry: false })
const actionsDialog = ref({ visible: false, user: null, confirmingDelete: false })
const telegramDialog = ref({ visible: false, loading: false, user: null, code: '', command: '', link: '', expiresAt: null })
const roleDialog = ref({ visible: false, loading: false, user: null, newRole: null, oldRole: null })
const roundsDialog = ref({ visible: false, user: null })
const addressDialog = ref({
  visible: false, loading: false, creating: false, user: null,
  selectedAddressIds: [],
  newName: '', newAddress: '', newCity: ''
})
const allAddresses = ref([])
const allInstructors = ref([])

const dayOptions = computed(() => [
  { title: t('days.monday') || 'Дүйсенбі', value: 1 },
  { title: t('days.tuesday') || 'Сейсенбі', value: 2 },
  { title: t('days.wednesday') || 'Сәрсенбі', value: 3 },
  { title: t('days.thursday') || 'Бейсенбі', value: 4 },
  { title: t('days.friday') || 'Жұма', value: 5 },
  { title: t('days.saturday') || 'Сенбі', value: 6 },
  { title: t('days.sunday') || 'Жексенбі', value: 0 }
])

const timeSlotOptions = [
  '08:00-10:00', '10:00-12:00', '12:00-14:00',
  '14:00-16:00', '16:00-18:00', '18:00-20:00'
]

const instructorProfileDialog = ref({
  visible: false,
  saving: false,
  uploadingPhoto: false,
  uploadingCarPhoto: false,
  user: null,
  instructorId: null,
  photoFile: null,
  carPhotoFile: null,
  photoPreview: '',
  carPhotoPreview: '',
  form: null
})

const getInstructorImageUrl = (filename) => {
  if (!filename) return ''
  if (/^https?:\/\//i.test(filename)) return filename
  return `/images/instructors/${filename}`
}

const parseInstructorJson = (val, fallback) => {
  if (!val) return fallback
  if (Array.isArray(val) || typeof val === 'object') return val
  try { return JSON.parse(val) } catch { return fallback }
}

const normalizeDays = (days) => {
  const map = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }
  return (days || []).map(d => typeof d === 'string' && isNaN(d) ? (map[d.toLowerCase()] ?? null) : Number(d)).filter(d => d !== null && !Number.isNaN(d))
}

const openInstructorProfileDialog = async (user) => {
  if (!user?.instructor) {
    snackbar.error(t('adminUsers.noInstructorProfile') || 'Инструктор профилі табылмады')
    return
  }
  const ins = user.instructor
  await loadAddresses()
  let existingAddressIds = []
  try {
    const res = await api.get(`/admin/instructors/${ins.id}`)
    existingAddressIds = Array.isArray(res.data?.addressIds) ? res.data.addressIds : []
  } catch { /* ignore */ }
  if (!existingAddressIds.length && ins.addressId) existingAddressIds = [ins.addressId]

  instructorProfileDialog.value = {
    visible: true,
    saving: false,
    uploadingPhoto: false,
    uploadingCarPhoto: false,
    user,
    instructorId: ins.id,
    photoFile: null,
    carPhotoFile: null,
    photoPreview: getInstructorImageUrl(ins.photo),
    carPhotoPreview: getInstructorImageUrl(ins.carPhoto),
    form: {
      carModel: ins.carModel || '',
      carNumber: ins.carNumber || '',
      description: ins.description || '',
      freeDays: normalizeDays(parseInstructorJson(ins.freeDays, [])),
      freeTime: (() => {
        const data = parseInstructorJson(ins.freeTime, [])
        if (Array.isArray(data)) return data
        if (data && typeof data === 'object') return [...new Set(Object.values(data).flat())]
        return []
      })(),
      addressIds: existingAddressIds
    }
  }
}

const onProfilePhotoSelect = async (kind) => {
  const dlg = instructorProfileDialog.value
  const file = kind === 'photo'
    ? (Array.isArray(dlg.photoFile) ? dlg.photoFile[0] : dlg.photoFile)
    : (Array.isArray(dlg.carPhotoFile) ? dlg.carPhotoFile[0] : dlg.carPhotoFile)
  if (!file) return

  const reader = new FileReader()
  reader.onload = (e) => {
    if (kind === 'photo') dlg.photoPreview = e.target.result
    else dlg.carPhotoPreview = e.target.result
  }
  reader.readAsDataURL(file)

  const fd = new FormData()
  fd.append('photo', file)
  const endpoint = kind === 'photo'
    ? `/admin/instructors/${dlg.instructorId}/photo`
    : `/admin/instructors/${dlg.instructorId}/car-photo`

  if (kind === 'photo') dlg.uploadingPhoto = true; else dlg.uploadingCarPhoto = true
  try {
    const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    if (kind === 'photo') {
      dlg.photoPreview = getInstructorImageUrl(res.data.filename)
      dlg.photoFile = null
    } else {
      dlg.carPhotoPreview = getInstructorImageUrl(res.data.filename)
      dlg.carPhotoFile = null
    }
    snackbar.success(t('adminUsers.photoUploaded') || 'Сурет жүктелді')
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    if (kind === 'photo') dlg.uploadingPhoto = false; else dlg.uploadingCarPhoto = false
  }
}

// ========= Bulk assign tests to an instructor group =========
const assignTestsDialog = ref({
  visible: false,
  loading: false,
  loadingTopics: false,
  saving: false,
  instructor: null,
  title: '',
  notes: '',
  topics: [],
  selectedTopics: [],
  selectedTestIds: [],
  students: [],
  studentSelection: new Set()
})

const allStudentsChecked = computed(() => {
  const total = assignTestsDialog.value.students.length
  if (!total) return false
  return assignTestsDialog.value.studentSelection.size === total
})

const openAssignTestsDialog = async (instructor) => {
  if (!instructor?.instructor?.id) {
    snackbar.error(t('adminUsers.noInstructorProfile') || 'Инструктор профилі табылмады')
    return
  }
  assignTestsDialog.value = {
    visible: true,
    loading: true,
    loadingTopics: true,
    saving: false,
    instructor,
    title: `Тест тағайындау — ${new Date().toLocaleDateString('ru-RU')}`,
    notes: '',
    topics: [],
    selectedTopics: [],
    selectedTestIds: [],
    students: [],
    studentSelection: new Set()
  }

  try {
    // Load this instructor's students
    const [s1, s2] = await Promise.all([
      api.get('/admin/users', { params: { role: 'STUDENT', limit: 500 } }),
      api.get('/admin/users', { params: { role: 'LIMITED_STUDENT', limit: 500 } })
    ])
    const all = [...(s1.data?.users || []), ...(s2.data?.users || [])]
    const group = all.filter(u => u.instructorId === instructor.instructor.id)
    assignTestsDialog.value.students = group
    assignTestsDialog.value.studentSelection = new Set(group.map(s => s.id))

    // Load topics
    const topicsRes = await api.get('/tests/topics').catch(() => ({ data: { topics: [] } }))
    const topics = topicsRes.data?.topics || []
    assignTestsDialog.value.topics = topics.map(t => ({
      title: `${t.topic || t.name || '—'}${t.count ? ` (${t.count})` : ''}`,
      value: t.topic || t.name
    }))
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    assignTestsDialog.value.loading = false
    assignTestsDialog.value.loadingTopics = false
  }
}

const onTopicChange = async () => {
  if (!assignTestsDialog.value.selectedTopics.length) {
    assignTestsDialog.value.selectedTestIds = []
    return
  }
  try {
    // Fetch test IDs for selected topics
    const ids = []
    for (const topic of assignTestsDialog.value.selectedTopics) {
      const res = await api.get('/tests', { params: { topic, limit: 1000 } })
      const tests = res.data?.tests || res.data || []
      tests.forEach(t => { if (t.id) ids.push(t.id) })
    }
    assignTestsDialog.value.selectedTestIds = [...new Set(ids)]
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const toggleAssignTestStudent = (id) => {
  const sel = assignTestsDialog.value.studentSelection
  if (sel.has(id)) sel.delete(id); else sel.add(id)
  assignTestsDialog.value.studentSelection = new Set(sel)
}

const toggleAllAssignTestStudents = () => {
  if (allStudentsChecked.value) {
    assignTestsDialog.value.studentSelection = new Set()
  } else {
    assignTestsDialog.value.studentSelection = new Set(assignTestsDialog.value.students.map(s => s.id))
  }
}

const submitBulkTestAssignment = async () => {
  const dlg = assignTestsDialog.value
  if (!dlg.instructor?.instructor?.id) return
  dlg.saving = true
  try {
    const res = await api.post(`/admin/instructors/${dlg.instructor.instructor.id}/assign-tests`, {
      testIds: dlg.selectedTestIds,
      title: dlg.title,
      notes: dlg.notes,
      studentIds: Array.from(dlg.studentSelection)
    })
    snackbar.success(
      `${res.data?.assignedCount || 0} ${t('adminUsers.studentsAssigned') || 'оқушыға тағайындалды'}` +
      (res.data?.failedCount ? `, ${res.data.failedCount} қате` : '')
    )
    dlg.visible = false
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    dlg.saving = false
  }
}

// ========= Bulk assign students =========
const assignStudentsDialog = ref({
  visible: false,
  loading: false,
  saving: false,
  instructor: null,
  allStudents: [],
  selected: new Set(),
  search: '',
  filter: 'ALL'
})

const assignFilterOptions = computed(() => [
  { title: t('common.all') || 'Барлығы', value: 'ALL' },
  { title: t('adminUsers.unassigned') || 'Тағайындалмаған', value: 'UNASSIGNED' },
  { title: t('adminUsers.currentlyAssigned') || 'Осы инструктордікі', value: 'MINE' },
  { title: t('adminUsers.otherInstructor') || 'Басқа инструктордікі', value: 'OTHER' }
])

const filteredAssignStudents = computed(() => {
  const term = (assignStudentsDialog.value.search || '').toLowerCase().trim()
  const instructorId = assignStudentsDialog.value.instructor?.instructor?.id
  return assignStudentsDialog.value.allStudents.filter(s => {
    if (term) {
      const haystack = `${s.fio || ''} ${s.phone || ''} ${s.email || ''}`.toLowerCase()
      if (!haystack.includes(term)) return false
    }
    switch (assignStudentsDialog.value.filter) {
      case 'UNASSIGNED': return !s.instructorId
      case 'MINE': return s.instructorId === instructorId
      case 'OTHER': return s.instructorId && s.instructorId !== instructorId
      default: return true
    }
  })
})

const openAssignStudentsDialog = async (instructor) => {
  if (!instructor?.instructor?.id) {
    snackbar.error(t('adminUsers.noInstructorProfile') || 'Инструктор профилі табылмады')
    return
  }
  assignStudentsDialog.value = {
    visible: true,
    loading: true,
    saving: false,
    instructor,
    allStudents: [],
    selected: new Set(),
    search: '',
    filter: 'ALL'
  }
  try {
    // Load students from two roles
    const [s1, s2] = await Promise.all([
      api.get('/admin/users', { params: { role: 'STUDENT', limit: 500 } }),
      api.get('/admin/users', { params: { role: 'LIMITED_STUDENT', limit: 500 } })
    ])
    const all = [...(s1.data?.users || []), ...(s2.data?.users || [])]
    // De-duplicate by id
    const map = new Map()
    all.forEach(u => map.set(u.id, u))
    assignStudentsDialog.value.allStudents = Array.from(map.values()).sort((a, b) => {
      return (a.fio || '').localeCompare(b.fio || '')
    })
    // Pre-select students already assigned to this instructor
    const instructorId = instructor.instructor.id
    assignStudentsDialog.value.selected = new Set(
      assignStudentsDialog.value.allStudents.filter(s => s.instructorId === instructorId).map(s => s.id)
    )
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    assignStudentsDialog.value.loading = false
  }
}

const toggleStudentSelect = (studentId) => {
  const sel = assignStudentsDialog.value.selected
  if (sel.has(studentId)) sel.delete(studentId); else sel.add(studentId)
  // Trigger reactivity for Set
  assignStudentsDialog.value.selected = new Set(sel)
}

const selectAllFilteredStudents = () => {
  const sel = new Set(assignStudentsDialog.value.selected)
  filteredAssignStudents.value.forEach(s => sel.add(s.id))
  assignStudentsDialog.value.selected = sel
}

const saveStudentAssignments = async () => {
  const dlg = assignStudentsDialog.value
  if (!dlg.instructor?.instructor?.id) return
  const instructorId = dlg.instructor.instructor.id
  const selectedIds = new Set(dlg.selected)

  dlg.saving = true
  try {
    // Selected → assign to this instructor; previously-assigned but now unselected → unassign
    const tasks = []
    for (const s of dlg.allStudents) {
      const wasAssigned = s.instructorId === instructorId
      const isSelected = selectedIds.has(s.id)
      if (isSelected && !wasAssigned) {
        tasks.push(api.put(`/admin/users/${s.id}`, { instructorId }))
      } else if (!isSelected && wasAssigned) {
        tasks.push(api.put(`/admin/users/${s.id}`, { instructorId: null }))
      }
    }
    if (!tasks.length) {
      snackbar.success(t('common.saved') || 'Сақталды')
      dlg.visible = false
      return
    }
    const results = await Promise.allSettled(tasks)
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed) {
      snackbar.error(`${failed} ${t('common.error') || 'Қате'}`)
    } else {
      snackbar.success(`${tasks.length} ${t('adminUsers.studentsAssigned') || 'оқушы жаңартылды'}`)
    }
    dlg.visible = false
    await loadUsers()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    dlg.saving = false
  }
}

// ========= Schedule management =========
const instructorTimeSlots = [
  '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
  '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
  '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00'
]

const scheduleStatusItems = computed(() => [
  { title: t('common.all') || 'Барлығы', value: 'ALL' },
  { title: 'SCHEDULED', value: 'SCHEDULED' },
  { title: 'IN_PROGRESS', value: 'IN_PROGRESS' },
  { title: 'COMPLETED', value: 'COMPLETED' },
  { title: 'CANCELLED', value: 'CANCELLED' }
])

const scheduleDialog = ref({
  visible: false,
  loading: false,
  user: null,
  instructorId: null,
  schedules: [],
  startDate: '',
  endDate: '',
  statusFilter: 'SCHEDULED',
  studentOptions: [],
  addForm: { visible: false, saving: false, studentId: null, date: '', timeSlot: '' }
})

const formatScheduleDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' })
}

const getScheduleStatusColor = (status) => {
  switch (status) {
    case 'SCHEDULED': return 'info'
    case 'IN_PROGRESS': return 'warning'
    case 'COMPLETED': return 'success'
    case 'CANCELLED': return 'error'
    default: return 'default'
  }
}

const filteredSchedules = computed(() => {
  if (scheduleDialog.value.statusFilter === 'ALL') return scheduleDialog.value.schedules
  return scheduleDialog.value.schedules.filter(s => s.status === scheduleDialog.value.statusFilter)
})

const openScheduleDialog = async (user) => {
  if (!user?.instructor?.id) {
    snackbar.error(t('adminUsers.noInstructorProfile') || 'Инструктор профилі табылмады')
    return
  }
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000)
  scheduleDialog.value = {
    visible: true,
    loading: false,
    user,
    instructorId: user.instructor.id,
    schedules: [],
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
    statusFilter: 'SCHEDULED',
    studentOptions: [],
    addForm: { visible: false, saving: false, studentId: null, date: '', timeSlot: '' }
  }
  await Promise.all([loadInstructorSchedule(), loadStudentsForInstructor(user.instructor.id)])
}

const loadInstructorSchedule = async () => {
  if (!scheduleDialog.value.instructorId) return
  scheduleDialog.value.loading = true
  try {
    const res = await api.get(`/admin/instructors/${scheduleDialog.value.instructorId}/schedule`, {
      params: {
        startDate: scheduleDialog.value.startDate,
        endDate: scheduleDialog.value.endDate
      }
    })
    scheduleDialog.value.schedules = res.data?.schedules || []
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    scheduleDialog.value.loading = false
  }
}

const loadStudentsForInstructor = async (instructorId) => {
  try {
    const res = await api.get('/admin/users', {
      params: { role: 'STUDENT', limit: 200 }
    })
    const users = res.data?.users || []
    const own = users.filter(u => u.instructorId === instructorId)
    scheduleDialog.value.studentOptions = (own.length ? own : users).map(u => ({
      title: `${u.fio} (${u.phone})`,
      value: u.id
    }))
  } catch { /* ignore */ }
}

const openAddBookingForm = () => {
  scheduleDialog.value.addForm = {
    visible: true,
    saving: false,
    studentId: null,
    date: scheduleDialog.value.startDate || new Date().toISOString().slice(0, 10),
    timeSlot: instructorTimeSlots[0]
  }
}

const createBooking = async () => {
  const f = scheduleDialog.value.addForm
  f.saving = true
  try {
    await api.post(`/admin/instructors/${scheduleDialog.value.instructorId}/schedule`, {
      studentId: f.studentId,
      date: f.date,
      timeSlot: f.timeSlot
    })
    snackbar.success(t('common.saved') || 'Сақталды')
    scheduleDialog.value.addForm.visible = false
    await loadInstructorSchedule()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    f.saving = false
  }
}

const shiftBookingBy15 = async (s) => {
  const m = String(s.timeSlot || '').match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/)
  if (!m) return
  const shift = (h, mm) => {
    const total = (Number(h) * 60 + Number(mm) + 15) % (24 * 60)
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  }
  const newSlot = `${shift(m[1], m[2])}-${shift(m[3], m[4])}`
  try {
    await api.put(`/admin/instructors/${scheduleDialog.value.instructorId}/schedule/${s.id}`, {
      timeSlot: newSlot
    })
    snackbar.success(`+15м → ${newSlot}`)
    await loadInstructorSchedule()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const cancelBooking = async (s) => {
  if (!confirm(`${s.student?.fio || ''} — ${s.timeSlot} болдырылсын ба?`)) return
  try {
    await api.put(`/admin/instructors/${scheduleDialog.value.instructorId}/schedule/${s.id}`, {
      status: 'CANCELLED'
    })
    snackbar.success(t('adminUsers.cancelled') || 'Болдырылмады')
    await loadInstructorSchedule()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const resetIncorrectQuestions = async (user) => {
  if (!user?.id) return
  if (!confirm(t('adminUsers.resetIncorrectConfirm', { name: user.fio }) || `${user.fio} оқушысының барлық қателерін тазалауға сенімдісіз бе?`)) return
  try {
    const res = await api.post(`/admin/users/${user.id}/reset-incorrect`)
    snackbar.success(res.data?.message || t('adminUsers.resetIncorrectDone') || 'Қателер тазаланды')
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const saveInstructorProfile = async () => {
  const dlg = instructorProfileDialog.value
  dlg.saving = true
  try {
    await api.put(`/admin/instructors/${dlg.instructorId}`, {
      carModel: dlg.form.carModel,
      carNumber: dlg.form.carNumber,
      description: dlg.form.description,
      freeDays: dlg.form.freeDays,
      freeTime: dlg.form.freeTime
    })
    if (Array.isArray(dlg.form.addressIds)) {
      await api.put(`/admin/instructors/${dlg.instructorId}/addresses`, {
        addressIds: dlg.form.addressIds
      })
    }
    snackbar.success(t('common.saved') || t('adminUsers.userUpdated'))
    dlg.visible = false
    await loadUsers()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    dlg.saving = false
  }
}

const toggleAllSchedulesPermission = async (user, value) => {
  if (!user || user.role !== 'INSTRUCTOR') return
  try {
    const { data } = await api.put(`/admin/users/${user.id}`, {
      canViewAllSchedules: Boolean(value)
    })
    const canViewAllSchedules = !!(data?.user?.canViewAllSchedules ?? value)
    user.canViewAllSchedules = canViewAllSchedules
    if (actionsDialog.value.user?.id === user.id) {
      actionsDialog.value.user.canViewAllSchedules = canViewAllSchedules
    }
    const row = users.value.find((item) => item.id === user.id)
    if (row) row.canViewAllSchedules = canViewAllSchedules
    snackbar.success(t('common.saved') || 'Сақталды')
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const inlineAddress = ref({
  visible: false,
  loading: false,
  name: '',
  address: '',
  city: ''
})

const closeInlineAddress = () => {
  inlineAddress.value = { visible: false, loading: false, name: '', address: '', city: '' }
}

const createInlineAddress = async () => {
  inlineAddress.value.loading = true
  try {
    const res = await api.post('/admin/addresses', {
      name: inlineAddress.value.name,
      address: inlineAddress.value.address,
      city: inlineAddress.value.city || null
    })
    allAddresses.value.push(res.data)
    editDialog.value.form.addressId = res.data.id
    snackbar.success(t('adminUsers.addressCreated'))
    closeInlineAddress()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    inlineAddress.value.loading = false
  }
}

const instructorOptions = computed(() => {
  return allInstructors.value.map(i => ({
    title: i.fio + (i.carModel ? ` — ${i.carModel}` : ''),
    value: i.id
  }))
})

const loadInstructors = async () => {
  try {
    const res = await api.get('/instructors')
    allInstructors.value = Array.isArray(res.data) ? res.data : (res.data?.instructors || [])
  } catch { /* ignore */ }
}

const addressOptions = computed(() => {
  return allAddresses.value.map(a => ({
    label: a.address,
    subtitle: a.city || '',
    value: a.id
  }))
})

const instructorAddresses = (user) => {
  const instructor = user?.instructor
  if (!instructor) return []
  if (Array.isArray(instructor.addresses) && instructor.addresses.length) return instructor.addresses
  return instructor.address ? [instructor.address] : []
}

const formatInstructorAddresses = (user) => {
  return instructorAddresses(user)
    .map(address => address?.address || address?.name)
    .filter(Boolean)
    .join(', ')
}

const loadAddresses = async () => {
  try {
    const res = await api.get('/admin/addresses')
    allAddresses.value = res.data
  } catch { /* ignore */ }
}

const openAddressDialog = async (user) => {
  addressDialog.value.user = user
  addressDialog.value.selectedAddressIds = Array.isArray(user.instructor?.addressIds)
    ? [...user.instructor.addressIds]
    : (Array.isArray(user.instructor?.addresses) && user.instructor.addresses.length
      ? user.instructor.addresses.map(address => address.id)
      : (user.instructor?.addressId ? [user.instructor.addressId] : []))
  addressDialog.value.newName = ''
  addressDialog.value.newAddress = ''
  addressDialog.value.newCity = ''
  addressDialog.value.visible = true
  await loadAddresses()
}

const createNewAddress = async () => {
  addressDialog.value.creating = true
  try {
    const res = await api.post('/admin/addresses', {
      name: addressDialog.value.newName,
      address: addressDialog.value.newAddress,
      city: addressDialog.value.newCity || null
    })
    allAddresses.value.push(res.data)
    addressDialog.value.selectedAddressIds = [...(addressDialog.value.selectedAddressIds || []), res.data.id]
    addressDialog.value.newName = ''
    addressDialog.value.newAddress = ''
    addressDialog.value.newCity = ''
    snackbar.success(t('adminUsers.addressCreated'))
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    addressDialog.value.creating = false
  }
}

const saveInstructorAddress = async () => {
  if (!addressDialog.value.user?.id) {
    snackbar.error(t('adminUsers.noInstructorProfile'))
    return
  }
  addressDialog.value.loading = true
  try {
    const res = await api.put(`/admin/users/${addressDialog.value.user.id}/instructor-address`, {
      addressIds: addressDialog.value.selectedAddressIds || []
    })
    // Жергілікті деректерді жаңарту
    const user = addressDialog.value.user
    user.instructor = res.data
    addressDialog.value.visible = false
    snackbar.success(t('adminUsers.addressSaved'))
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    addressDialog.value.loading = false
  }
}

const openRoundsDialog = (user) => {
  console.log('Rounds data:', JSON.stringify(user.testStats?.rounds))
  roundsDialog.value.user = user
  roundsDialog.value.visible = true
}

const openActionsDialog = (user) => {
  actionsDialog.value.user = user
  actionsDialog.value.confirmingDelete = false
  actionsDialog.value.visible = true
}

// Phone validation rules
const phoneRules = [
  v => !!v || t('adminUsers.phoneRequired'),
  v => {
    const digits = (v || '').replace(/\D/g, '')
    return (digits.length === 11 && digits.startsWith('7') && v.startsWith('+7')) || t('adminUsers.phoneFormat')
  }
]

// Email validation rules (optional but must be valid format if entered)
const emailRules = [
  v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || t('adminUsers.emailFormat')
]

const normalizingPhones = ref(false)
const phoneConflictsDialog = ref(false)
const phoneConflicts = ref([])
const normalizePhones = async () => {
  if (!confirm(t('adminUsers.fixPhonesConfirm'))) return
  normalizingPhones.value = true
  try {
    const res = await api.post('/admin/users/normalize-phones')
    const { fixed = 0, skipped = 0, conflicts = [] } = res.data || {}
    snackbar.success(t('adminUsers.fixPhonesDone', { fixed, skipped }))
    await loadUsers()
    if (conflicts.length) {
      phoneConflicts.value = conflicts
      phoneConflictsDialog.value = true
    }
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    normalizingPhones.value = false
  }
}
const deleteConflictUser = async (c) => {
  if (!confirm(t('adminUsers.fixPhonesDeleteConfirm', { phone: c.phone }))) return
  try {
    await api.delete(`/admin/users/${c.id}`)
    snackbar.success(t('common.deleted') || 'Өшірілді')
    phoneConflicts.value = phoneConflicts.value.filter(x => x.id !== c.id)
    await loadUsers()
    if (!phoneConflicts.value.length) phoneConflictsDialog.value = false
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

const loadUsers = async () => {
  try {
    loading.value = true
    const params = {
      page: pagination.value.page,
      limit: pagination.value.limit,
      role: filters.value.role || undefined,
      status: filters.value.status || undefined,
      search: filters.value.search || undefined,
      sortBy: filters.value.sortBy || undefined,
      sortOrder: filters.value.sortOrder || 'desc'
    }
    const { data } = await api.get('/admin/users', { params })
    console.log('📊 Loaded users:', data.users?.length, 'Total:', data.pagination?.total, 'Online:', data.onlineCount)
    // Force Vue reactivity by replacing entire array
    users.value = [...(data.users || [])]
    onlineCount.value = data.onlineCount || 0
    pagination.value = {
      ...pagination.value,
      total: data.pagination?.total || 0,
      totalPages: data.pagination?.totalPages || 1
    }
  } catch (e) {
    console.error('❌ Load users error:', e)
    snackbar.error(t('adminUsers.loadError'))
  } finally {
    loading.value = false
  }
}

const applyFilters = () => {
  pagination.value.page = 1
  loadUsers()
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 400)
}

// Онлайн бойынша сұрыптау
const sortByOnline = () => {
  filters.value.sortBy = 'online'
  filters.value.sortOrder = 'desc'
  tableSortBy.value = [{ key: 'isOnline', order: 'desc' }]
  applyFilters()
}

// Сұрыптауды алып тастау
const clearSort = () => {
  filters.value.sortBy = 'lastLogin'
  filters.value.sortOrder = 'desc'
  tableSortBy.value = [{ key: 'lastSession', order: 'desc' }]
  applyFilters()
}

// v-data-table-server сұрыптау өзгергенде
const handleSort = (newSortBy) => {
  tableSortBy.value = newSortBy
  if (newSortBy.length > 0) {
    const { key, order } = newSortBy[0]
    filters.value.sortBy = sortKeyMap[key] || key
    filters.value.sortOrder = order
  } else {
    filters.value.sortBy = 'lastLogin'
    filters.value.sortOrder = 'desc'
  }
  pagination.value.page = 1
  loadUsers()
}

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    snackbar.success(t('common.copied'))
  } catch (e) {
    snackbar.error(t('common.copyFailed'))
  }
}

// Mobile formatting functions
const formatExpiryDateShort = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('kz-KZ', {
    day: '2-digit',
    month: '2-digit'
  })
}

const formatSessionTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()
  
  const time = date.toLocaleTimeString('kk-KZ', { hour: '2-digit', minute: '2-digit' })
  
  if (isToday) return `${t('common.today')} ${time}`
  if (isYesterday) return `${t('common.yesterday')} ${time}`
  return date.toLocaleDateString('kk-KZ', { day: '2-digit', month: '2-digit' }) + ' ' + time
}

const formatShortDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('kk-KZ', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('kk-KZ', { hour: '2-digit', minute: '2-digit' })
}

const formatShortDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yy = String(date.getFullYear()).slice(-2)
  return `${dd}.${mm}.${yy}`
}

// UserAgent-ті парсинг — құрылғы типі, ОЖ, браузер
const parseUserAgent = (ua) => {
  if (!ua) return null
  const s = ua.toLowerCase()
  
  // Құрылғы типі
  const isMobile = /mobile|android|iphone|ipad|ipod/i.test(ua)
  const isTablet = /ipad|tablet/i.test(ua)
  let deviceIcon = 'mdi-monitor' // desktop
  let deviceColor = 'blue-grey'
  if (isTablet) { deviceIcon = 'mdi-tablet'; deviceColor = 'purple' }
  else if (isMobile) { deviceIcon = 'mdi-cellphone'; deviceColor = 'teal' }

  // ОЖ
  let os = ''
  if (s.includes('iphone') || s.includes('ipad') || s.includes('ipod')) os = 'iOS'
  else if (s.includes('mac os')) os = 'macOS'
  else if (s.includes('android')) os = 'Android'
  else if (s.includes('windows')) os = 'Windows'
  else if (s.includes('linux')) os = 'Linux'

  // Браузер
  let browser = ''
  let browserIcon = 'mdi-web'
  if (s.includes('edg/') || s.includes('edga/') || s.includes('edgios/')) { browser = 'Edge'; browserIcon = 'mdi-microsoft-edge' }
  else if (s.includes('opr/') || s.includes('opera')) { browser = 'Opera'; browserIcon = 'mdi-opera' }
  else if (s.includes('yabrowser')) { browser = 'Yandex'; browserIcon = 'mdi-alpha-y-circle' }
  else if (s.includes('crios') || (!s.includes('edg') && s.includes('chrome'))) { browser = 'Chrome'; browserIcon = 'mdi-google-chrome' }
  else if (s.includes('fxios') || s.includes('firefox')) { browser = 'Firefox'; browserIcon = 'mdi-firefox' }
  else if (s.includes('safari') && !s.includes('chrome')) { browser = 'Safari'; browserIcon = 'mdi-apple-safari' }

  // Қысқа мәтін
  const label = [os, browser].filter(Boolean).join(' ')
  
  return { deviceIcon, deviceColor, browserIcon, browser, os, label, isMobile }
}

const getExpiryChipColor = (expiryDate) => {
  if (!expiryDate) return 'grey'
  
  const today = new Date()
  const expiry = new Date(expiryDate)
  const diffTime = expiry - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays <= 0) return 'error'
  if (diffDays <= 3) return 'warning'
  if (diffDays <= 7) return 'primary'
  return 'success'
}

const getStatusColor = (status) => {
  switch (status) {
    case 'ACTIVE': return 'success'
    case 'PENDING': return 'warning'
    case 'BLOCKED': return 'error'
    default: return 'default'
  }
}

const getStatusText = (status) => {
  // Map status via i18n
  return t(`status.${status}`) || status
}

const savePreviousStatus = (item) => {
  item._previousStatus = item.status
}

const updateUserStatus = async (item, newStatus) => {
  const oldStatus = item._previousStatus || item.status
  if (!newStatus || newStatus === oldStatus) return

  try {
    await api.put(`/admin/users/${item.id}`, { status: newStatus })
    item.status = newStatus
    item._previousStatus = newStatus
    snackbar.success(t('adminUsers.userUpdated'))
  } catch (e) {
    item.status = oldStatus
    snackbar.error(t('messages.errorOccurred'))
  }
}

const openCreateDialog = async () => {
  editDialog.value = {
    visible: true,
    mode: 'create',
    loading: false,
    form: {
      id: null,
      fio: '',
      phone: '',
      email: '',
      password: '',
      role: 'STUDENT',
      status: 'ACTIVE',
      unlimitedAccess: true,
      expiryDate: null,
      addressId: null,
      instructorId: null
    }
  }
  closeInlineAddress()
  await Promise.all([loadAddresses(), loadInstructors()])
}
const openEditDialog = (item) => {
  editDialog.value = { 
    visible: true, 
    mode: 'edit', 
    loading: false, 
    form: { 
      id: item.id, 
      fio: item.fio || '', 
      phone: item.phone || '', 
      email: item.email || '', 
      role: item.role || 'STUDENT', 
      status: item.status || 'ACTIVE',
      unlimitedAccess: !item.expiryDate,
      expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : null
    } 
  }
}
const closeEditDialog = () => { 
  editDialog.value.visible = false
  showPassword.value = false
}

const saveUser = async () => {
  try {
    editDialog.value.loading = true
    const f = editDialog.value.form
    
    // Normalize phone number before sending
    let normalizedPhone = (f.phone || '').replace(/\D/g, '')
    if (normalizedPhone.startsWith('8') && normalizedPhone.length === 11) {
      normalizedPhone = '7' + normalizedPhone.slice(1)
    }
    if (!normalizedPhone.startsWith('7')) {
      normalizedPhone = '7' + normalizedPhone
    }
    normalizedPhone = '+' + normalizedPhone
    
    // Prepare expiry date
    const expiryDate = f.unlimitedAccess ? null : (f.expiryDate || null)
    
    if (editDialog.value.mode === 'create') {
      const payload = {
        fio: f.fio,
        phone: normalizedPhone,
        email: f.email,
        password: f.password,
        role: f.role,
        expiryDate: expiryDate
      }
      if (f.role === 'INSTRUCTOR' && f.addressId) payload.addressId = f.addressId
      if (['STUDENT', 'LIMITED_STUDENT'].includes(f.role) && f.instructorId) payload.instructorId = f.instructorId
      const { data } = await api.post('/admin/users', payload)
      snackbar.success(t('adminUsers.userCreated'))

      // Auto-open instructor profile editor for the new instructor
      if (f.role === 'INSTRUCTOR' && data?.user?.instructor) {
        editDialog.value.visible = false
        await loadUsers()
        openInstructorProfileDialog(data.user)
        return
      }
    } else {
      await api.put(`/admin/users/${f.id}`, { 
        fio: f.fio, 
        phone: normalizedPhone,
        email: f.email, 
        role: f.role, 
        status: f.status,
        expiryDate: expiryDate
      })
      snackbar.success(t('adminUsers.userUpdated'))
    }
    editDialog.value.visible = false
    await loadUsers()
  } catch (e) {
    // Error
    snackbar.error(t('messages.errorOccurred'))
  } finally {
    editDialog.value.loading = false
  }
}

const confirmDelete = (item) => {
  deleteDialog.value = { visible: true, loading: false, user: item }
}
const doDelete = async () => {
  try {
    deleteDialog.value.loading = true
  await api.delete(`/admin/users/${deleteDialog.value.user.id}`)
  snackbar.success(t('adminUsers.userDeleted'))
    deleteDialog.value.visible = false
    await loadUsers()
  } catch (e) {
    // Error
    snackbar.error(t('messages.errorOccurred'))
  } finally {
    deleteDialog.value.loading = false
  }
}

const sendTempPassword = async (item) => {
  try {
    // Generate and set temp password via backend, then WhatsApp minimal message
    const { data } = await api.post(`/admin/users/${item.id}/reset-password`)
    const tempPassword = data.tempPassword
    // open WhatsApp minimal message
    const cleanPhone = (item.phone || '').replace(/[\+\s\-\(\)]/g, '')
    const message = `Сәлеметсіз бе, ${item.fio}!

Уақытша пароль: ${tempPassword}
Сайтқа кіріп, жаңа пароль орнатыңыз.

Құрметпен, Көркем Авто`
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
    snackbar.success(t('adminUsers.tempPasswordSent'))
  } catch (e) {
    // Error
    snackbar.error(t('adminUsers.tempPasswordError'))
  }
}

const sendForceRefresh = async (item) => {
  if (!item?.id) return

  try {
    await api.post(`/admin/users/${item.id}/force-refresh`)
    snackbar.success(t('adminUsers.forceRefreshSent'))
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('adminUsers.forceRefreshError'))
  }
}

const generateTelegramCode = async (item) => {
  if (!item?.id) return

  try {
    telegramDialog.value = {
      visible: true,
      loading: true,
      user: item,
      code: '',
      command: '',
      link: '',
      expiresAt: null
    }
    const res = await api.post(`/admin/users/${item.id}/telegram-code`)
    const code = res.data?.code
    if (code) {
      telegramDialog.value.code = code
      telegramDialog.value.command = res.data?.command || `/link ${code}`
      telegramDialog.value.link = res.data?.link || `https://t.me/korkem_auto_bot?start=${encodeURIComponent(code)}`
      telegramDialog.value.expiresAt = res.data?.expiresAt || null
      await copyTelegramValue(telegramDialog.value.link, false)
      snackbar.success('Telegram сілтеме жасалды және көшірілді')
    } else {
      snackbar.success('Telegram код жасалды')
    }
  } catch (e) {
    telegramDialog.value.visible = false
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    telegramDialog.value.loading = false
  }
}

const telegramShareText = computed(() => {
  if (!telegramDialog.value.code) return ''
  return [
    'Telegram ботқа қосылу:',
    telegramDialog.value.link,
    '',
    `Команда: ${telegramDialog.value.command}`,
    `Код: ${telegramDialog.value.code}`
  ].join('\n')
})

const copyTelegramValue = async (value, showToast = true) => {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    if (showToast) snackbar.success(t('common.copied'))
  } catch {
    snackbar.error(t('common.copyFailed'))
  }
}

const openTelegramLink = () => {
  if (!telegramDialog.value.link) return
  window.open(telegramDialog.value.link, '_blank', 'noopener,noreferrer')
}

const getRoleTitle = (roleValue) => {
  const role = roleItems.value.find(r => r.value === roleValue)
  return role ? role.title : roleValue
}

const previousRole = ref(null)

const savePreviousRole = (item) => {
  previousRole.value = item.role
}

const confirmRoleChange = (item, newRole) => {
  roleDialog.value = {
    visible: true,
    loading: false,
    user: item,
    newRole: newRole,
    oldRole: previousRole.value
  }
}

const cancelRoleChange = () => {
  if (roleDialog.value.user && roleDialog.value.oldRole) {
    roleDialog.value.user.role = roleDialog.value.oldRole
  }
  roleDialog.value.visible = false
}

const applyRoleChange = async () => {
  roleDialog.value.loading = true
  try {
    const res = await api.put(`/admin/users/${roleDialog.value.user.id}`, { role: roleDialog.value.newRole })
    roleDialog.value.user.role = roleDialog.value.newRole
    if (res.data?.user?.instructor !== undefined) {
      roleDialog.value.user.instructor = res.data.user.instructor
      roleDialog.value.user.instructorId = res.data.user.instructorId
    }
    snackbar.success(t('adminUsers.roleUpdated'))
  } catch (e) {
    // Revert on error
    if (roleDialog.value.oldRole) {
      roleDialog.value.user.role = roleDialog.value.oldRole
    }
    snackbar.error(t('messages.errorOccurred'))
  } finally {
    roleDialog.value.loading = false
    roleDialog.value.visible = false
  }
}

const changeRole = async (item, newRole) => {
  try {
    await api.put(`/admin/users/${item.id}`, { role: newRole })
    snackbar.success(t('adminUsers.roleUpdated'))
  } catch (e) {
    // Error
    snackbar.error(t('messages.errorOccurred'))
  }
}

const openPasswordDialog = (item) => {
  passwordDialog.value = {
    visible: true,
    loading: false,
    user: item,
    newPassword: '',
    confirmPassword: ''
  }
}

const closePasswordDialog = () => {
  passwordDialog.value.visible = false
  passwordDialog.value.newPassword = ''
  passwordDialog.value.confirmPassword = ''
  showNewPassword.value = false
  showConfirmPassword.value = false
}

const changePassword = async () => {
  try {
    if (passwordDialog.value.newPassword.length < 6) {
      snackbar.error(t('validation.minLength', { min: 6 }) || 'Min 6')
      return
    }
    
    if (passwordDialog.value.newPassword !== passwordDialog.value.confirmPassword) {
      snackbar.error(t('validation.passwordMatch'))
      return
    }

    passwordDialog.value.loading = true
    await api.put(`/admin/users/${passwordDialog.value.user.id}/password`, {
      newPassword: passwordDialog.value.newPassword
    })
    
    snackbar.success(t('auth.passwordChanged'))
    closePasswordDialog()
  } catch (e) {
    // Error
    snackbar.error(e.response?.data?.error || (t('messages.errorOccurred')))
  } finally {
    passwordDialog.value.loading = false
  }
}

// Expiry management functions
const openExpiryDialog = (item) => {
  const currentExpiry = item.expiryDate 
    ? new Date(item.expiryDate).toISOString().split('T')[0] 
    : null
  expiryDialog.value = {
    visible: true,
    loading: false,
    user: item,
    expiryDate: currentExpiry,
    removeExpiry: !item.expiryDate
  }
}

const closeExpiryDialog = () => {
  expiryDialog.value.visible = false
}

const updateExpiry = async () => {
  try {
    expiryDialog.value.loading = true
    
    const expiryData = expiryDialog.value.removeExpiry 
      ? { expiryDate: null } 
      : { expiryDate: expiryDialog.value.expiryDate }
    
    await api.put(`/admin/users/${expiryDialog.value.user.id}/expiry`, expiryData)
    
    // Update local user data
    const userIndex = users.value.findIndex(u => u.id === expiryDialog.value.user.id)
    if (userIndex !== -1) {
      users.value[userIndex].expiryDate = expiryData.expiryDate
    }
    
    snackbar.success(expiryDialog.value.removeExpiry ? (t('adminUsers.expiryRemoved')) : (t('adminUsers.expirySetSuccess')))
    closeExpiryDialog()
  } catch (e) {
    // Error
    snackbar.error(e.response?.data?.error || (t('adminUsers.expirySetError')))
  } finally {
    expiryDialog.value.loading = false
  }
}

const formatExpiryDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('kz-KZ', {
    year: 'numeric',
    month: 'short', 
    day: 'numeric'
  })
}



// Socket listener for real-time test status updates
const handleTestStatus = (data) => {
  const { userId, status, ...testInfo } = data
  const user = users.value.find(u => u.id === userId)
  if (user) {
    user.isInTest = status === 'testing'
    user.testInfo = status === 'testing' ? testInfo : null
  }
}

onMounted(() => {
  loadUsers()
  
  // Socket listener қосу
  if (socketStore.socket) {
    socketStore.socket.on('user:test_status', handleTestStatus)
  }
})

onUnmounted(() => {
  // Socket listener алып тастау
  if (socketStore.socket) {
    socketStore.socket.off('user:test_status', handleTestStatus)
  }
})
</script>

<style scoped>
.gap-2 { gap: 8px; }

/* Compact table styles */
.compact-table :deep(th) {
  font-size: 0.75rem !important;
  padding: 4px 8px !important;
  white-space: nowrap;
}
.compact-table :deep(td) {
  font-size: 0.8rem !important;
  padding: 4px 8px !important;
}
.compact-table :deep(.v-data-table__td) {
  height: auto !important;
  min-height: 36px;
}

/* Compact role select in table */
.compact-role-select :deep(.v-field__input) {
  font-size: 0.75rem !important;
  padding: 2px 0 !important;
  min-height: 28px !important;
}
.compact-role-select :deep(.v-field) {
  padding-inline-end: 4px !important;
}
.compact-role-select :deep(.v-select__menu-icon) {
  margin-inline-start: 0 !important;
}

.telegram-linked-icon {
  flex-shrink: 0;
  opacity: 0.95;
}

.telegram-link-block {
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 8px;
  padding: 10px 12px;
  background: rgba(var(--v-theme-surface), 0.55);
}

.telegram-link-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.telegram-link-row code {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
  font-size: 0.82rem;
}

.address-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 16px;
}

.address-row .address-select {
  flex: 1 1 auto;
  min-width: 0;
}

.address-row .address-add-btn {
  flex: 0 0 auto;
  height: 40px !important;
  width: 40px !important;
  align-self: center;
}

.add-booking-form {
  background: rgba(33, 150, 243, 0.08);
  border-bottom: 1px solid rgba(33, 150, 243, 0.2);
}

.profile-photo-box {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  height: 150px;
  background: rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.compact-status-select {
  max-width: 135px;
}

.compact-status-select :deep(.v-field__input) {
  font-size: 0.75rem !important;
  padding: 2px 0 !important;
  min-height: 28px !important;
}

.compact-status-select :deep(.v-field) {
  padding-inline-end: 4px !important;
}

.compact-status-select :deep(.v-select__menu-icon) {
  margin-inline-start: 0 !important;
}

.mobile-status-select {
  width: 132px;
}

/* ============================================================
   User card — clean two-column header
   - Left: online dot + name (allowed to wrap) + ID below
   - Right: compact status chip (top-aligned, fixed narrow column)
   ============================================================ */
.user-card-body { padding: 14px 14px 10px !important; }

.user-card-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.user-card-name-block {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.user-online-dot {
  margin-top: 5px;
}

.user-name-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.user-name {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.1px;
  color: inherit;
  word-break: break-word;
  overflow-wrap: anywhere;
  /* Allow up to 2 lines, then ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.user-id {
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  margin-top: 2px;
}

/* Status chip column — no border, just chip + tiny arrow */
.user-status-select {
  width: auto !important;
  min-width: 92px;
  max-width: 112px;
  flex-shrink: 0;
}
.user-status-select :deep(.v-field) {
  background: transparent !important;
  box-shadow: none !important;
}
.user-status-select :deep(.v-field__input) {
  padding: 0 !important;
  min-height: 24px !important;
}
.user-status-select :deep(.v-field__append-inner) {
  padding-inline-start: 2px !important;
}
.user-status-select :deep(.v-field__append-inner .v-icon) {
  font-size: 16px !important;
  opacity: 0.55;
}

/* Compact status select on mobile so the user name has more room */
@media (max-width: 600px) {
  .mobile-status-select {
    width: auto !important;
    max-width: 110px;
    min-width: 88px;
  }

  /* Hide the outlined frame — the chip already conveys the status */
  .mobile-status-select :deep(.v-field) {
    background: transparent !important;
    box-shadow: none !important;
  }
  .mobile-status-select :deep(.v-field__outline) {
    --v-field-border-opacity: 0 !important;
    --v-field-border-width: 0 !important;
  }
  .mobile-status-select :deep(.v-field__outline__start),
  .mobile-status-select :deep(.v-field__outline__end),
  .mobile-status-select :deep(.v-field__outline__notch::before),
  .mobile-status-select :deep(.v-field__outline__notch::after) {
    border: none !important;
  }

  /* Tighter inner padding around the chip */
  .mobile-status-select :deep(.v-field__input) {
    padding: 0 !important;
    min-height: 28px !important;
  }
  .mobile-status-select :deep(.v-field__append-inner) {
    padding-inline-start: 2px !important;
  }
  .mobile-status-select :deep(.v-field__append-inner .v-icon) {
    font-size: 16px !important;
    opacity: 0.55;
  }
}

/* Тест тапсырып жатыр анимациясы */
.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Mobile user cards */
.user-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px;
}

/* Ensure text truncation works properly */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.min-width-0 {
  min-width: 0;
}

/* Mobile responsive adjustments */
@media (max-width: 600px) {
  .user-cards {
    gap: 10px;
    padding: 2px;
  }

  .user-cards .v-card {
    margin-bottom: 0;
  }
  
  .user-cards .v-card-text {
    padding: 10px 12px !important;
  }
  
  .user-cards .v-card-actions {
    padding: 6px 12px 10px 12px !important;
    flex-wrap: wrap;
    gap: 6px;
  }
  
  .user-cards .v-btn {
    font-size: 0.8rem;
    min-width: auto;
  }

  .user-cards .v-select .v-field {
    padding: 0 8px !important;
    min-height: 32px !important;
    font-size: 0.8rem !important;
  }

  .user-cards .v-chip {
    font-size: 0.65rem !important;
  }
}

/* Very small screens */
@media (max-width: 380px) {
  .user-cards .v-card-text {
    padding: 8px !important;
  }

  .user-cards .v-card-actions .v-btn:not(.v-btn--icon) {
    font-size: 0.75rem;
    padding: 4px 8px;
  }
  
  .user-cards .v-select {
    font-size: 0.75rem;
  }
}
</style>
